/**
 * @aiai/shared — single source of truth for cross-package contracts.
 *
 * Imported by the API (NestJS), the web client (React), and the agent runtime.
 * Keep this package dependency-free and runtime-agnostic: pure TS types + a few
 * tiny enums/const objects. No Node or browser APIs.
 */

// Workspace slug rules (slugify / isValidSlug / reserved + offensive blocklist)
// — shared so the API validates authoritatively and the web validates inline
// with the exact same list.
export * from './slug';
export * from './capabilities';
// Workspace plans & entitlements (basic|pro, credential rail, quotas) — the
// pure resolver + DTOs shared by API enforcement and the web plan/usage UI.
export * from './entitlements';
// Prototype design-style catalog — 100 curated "design.md" briefs the web
// gallery renders and the API appends to prototype briefs / restyle issues.
export * from './designs';
// Agent activity (newest tool call → read/edit/test/ship/…) for the hub astronauts.
export * from './activity';
// Project-scoped Integration API (external sites: submit request, poll status, feedback).
export * from './integrations';
import type { ProjectRole } from './capabilities';
import type { HubActivityDto } from './activity';
import type {
  OrgPlan,
  Entitlements,
  EntitlementUsage,
  ResolvedFeatures,
  FeatureOverrides,
  FeatureOverridePatch,
} from './entitlements';

// ─────────────────────────────────────────────────────────────────────────────
// Enums (string unions kept as const objects so they survive to runtime)
// ─────────────────────────────────────────────────────────────────────────────

/** Lifecycle of an issue. Exactly two states — run-execution state lives on Run. */
export const IssueStatus = {
  Open: 'open',
  Closed: 'closed',
} as const;
export type IssueStatus = (typeof IssueStatus)[keyof typeof IssueStatus];

/** Why a closed issue was closed. `null` while open. */
export const IssueResolution = {
  /** Work done / PR merged. */
  Fixed: 'fixed',
  /** Declined / out of scope. */
  WontDo: 'wont_do',
  /** Already covered by another issue (see sourceIssueId). */
  Duplicate: 'duplicate',
  /** Feedback triaged into a new backlog plan (see sourceIssueId). */
  Planned: 'planned',
  /** Feedback decomposed into child issues; this parent is superseded by them. */
  Decomposed: 'decomposed',
} as const;
export type IssueResolution = (typeof IssueResolution)[keyof typeof IssueResolution];

/**
 * The single lifecycle axis of an issue. Declared in board-sort order
 * (needs-you → problem → active → idle → done) so a Postgres enum column
 * orders correctly with `orderBy: { state: 'asc' }` and needs no separate rank.
 */
export const IssueState = {
  /** Needs a human go/no-go before anything runs (was status=open, approved=false). */
  Proposed: 'proposed',
  /** Build produced a PR held for a human to merge. */
  Review: 'review',
  /** Last attempt didn't stick (run gave up, or PR rejected). Resting — not buildable. */
  Failed: 'failed',
  /** A run is live. */
  Building: 'building',
  /** Approved, idle, buildable. */
  Open: 'open',
  /** Terminal; `resolution` records why. */
  Closed: 'closed',
} as const;
export type IssueState = (typeof IssueState)[keyof typeof IssueState];

export interface DeriveIssueStateInput {
  /** The issue's currently-persisted state — guards `proposed`/`closed`. */
  current: string;
  /**
   * The latest BUILD-lane run inside the issue's current state window — created
   * at/after `Issue.requeuedAt` (see the API's `latestRunInWindow`). Runs from
   * before the latest release/requeue are superseded and never passed in.
   */
  latestRun?: { status: string; pullRequestState: string } | null;
}

/**
 * Project an issue's `state` from its current state + latest in-window build
 * run/PR. Owns the approved band (`open ↔ building ↔ review ↔ failed ↔
 * closed-on-merge`) plus one move back out of it: a CANCELLED latest build
 * shelves the issue to `proposed` (Cancel = shelve — never auto-retried).
 * Never resurrects a `proposed` or `closed` issue. Single source of truth,
 * denormalized onto Issue.state so the board paginates in SQL.
 */
export function deriveIssueState(input: DeriveIssueStateInput): IssueState {
  if (input.current === IssueState.Proposed) return IssueState.Proposed;
  if (input.current === IssueState.Closed) return IssueState.Closed;
  const run = input.latestRun ?? null;
  // No build run in the window. A platform give-up (BuildRetryService) parks the
  // issue in `failed` with a fresh, empty window — it stays there until a human
  // releases it again; anything else with no run is idle and buildable.
  if (!run) return input.current === IssueState.Failed ? IssueState.Failed : IssueState.Open;
  const s = run.status;
  if (s === RunStatus.Pending || s === RunStatus.Provisioning || s === RunStatus.Running) {
    return IssueState.Building;
  }
  if (s === RunStatus.Succeeded) {
    if (run.pullRequestState === PullRequestState.Merged) return IssueState.Closed;
    if (run.pullRequestState === PullRequestState.Closed) return IssueState.Failed;
    return IssueState.Review;
  }
  // Cancel = shelve: a human stopped the build — the issue goes back to `proposed`.
  if (s === RunStatus.Cancelled) return IssueState.Proposed;
  return IssueState.Failed; // failed | timed_out
}

/** Lifecycle of a single agent run (one container execution). */
export const RunStatus = {
  Pending: 'pending',
  Provisioning: 'provisioning',
  Running: 'running',
  Succeeded: 'succeeded',
  Failed: 'failed',
  Cancelled: 'cancelled',
  TimedOut: 'timed_out',
} as const;
export type RunStatus = (typeof RunStatus)[keyof typeof RunStatus];

export const TERMINAL_RUN_STATUSES: RunStatus[] = [
  RunStatus.Succeeded,
  RunStatus.Failed,
  RunStatus.Cancelled,
  RunStatus.TimedOut,
];

/**
 * Live state of a run's GitHub pull request, as last observed on GitHub —
 * distinct from `RunStatus`/`IssueStatus`, which only reflect whether the
 * *agent* finished, not whether the resulting PR was merged, rejected, or is
 * still awaiting review. Synced by the API's PR sync service.
 */
export const PullRequestState = {
  /** No PR was opened for this run. */
  None: 'none',
  Open: 'open',
  Merged: 'merged',
  Closed: 'closed',
} as const;
export type PullRequestState =
  (typeof PullRequestState)[keyof typeof PullRequestState];

/**
 * Specialized agent roles executed as ordered stages of the loop.
 * Each role maps to a system-prompt + tool policy in the API's role registry.
 * Worker is the sole active role; historical run data may still reference the
 * now-deleted roles ('autonomous', 'reporter', 'researcher') — those strings
 * must be tolerated at render time but are no longer used for new runs.
 */
export const AgentRole = {
  /** Full-toolkit worker: reads the board + repo, does its mission, may write
   *  code (→ PR), create issues, and manage existing issues. */
  Worker: 'worker',
  /** Product Manager: triages the feedback inbox and reviews the app, then files
   *  planned `implement` tickets or closes weak feedback with a comment. It has
   *  NO code/PR tools — a planner run can never open a pull request. */
  Planner: 'planner',
  /** Project-chat PM: read-only conversational turn; writes ONE reply via
   *  /aiai/chat-reply.json and can take no board or code action. */
  Chat: 'chat',
} as const;
export type AgentRole = (typeof AgentRole)[keyof typeof AgentRole];

/**
 * The fixed team roster shown on a project's Team tab, in display order. These
 * mirror the hardcoded role registry — they are NOT editable (no prompts, no
 * schedules) and always present; the per-project `autonomy` dial governs what
 * they may do without you.
 */
export interface AgentCatalogEntry {
  role: AgentRole;
  name: string;
  tagline: string;
  description: string;
}

export const AGENT_CATALOG: readonly AgentCatalogEntry[] = [
  {
    role: AgentRole.Planner,
    name: 'Product Manager',
    tagline: 'Plans and triages incoming tickets, answers in the thread.',
    description:
      'Picks up every filed ticket: writes up an implementation plan for approval, asks clarifying questions in the thread, marks duplicates, or closes what is not worth doing. It recommends — it never authorizes work itself.',
  },
  {
    role: AgentRole.Worker,
    name: 'Engineer',
    tagline: 'Builds approved work and opens pull requests.',
    description:
      'Picks up a ticket as soon as it is released to build, implements it in an isolated container against the test suite, and opens a pull request.',
  },
];

/** The per-project Team dial — how much the always-present team (Product
 *  Manager + Engineer) does without you. `autopilot` (default) = nothing waits
 *  for a human: the PM plans every ticket and its plan always releases to the
 *  Engineer (the risk net is recorded for audit, never holds). `manual` =
 *  PAUSED (the UI label) — a pause switch, not an approval step: no reactive
 *  PM turns, no auto-builds, no loop firings; only explicit Approve/Run moves
 *  work. `gated` was removed 2026-09-27 (zero-touch autonomy). */
export type ProjectAutonomy = 'manual' | 'autopilot';

/**
 * Per-project gate behaviour when the test suite keeps failing after all
 * automatic retries are exhausted.
 *
 * `strict` (default) — fail the run immediately (no PR); nothing waits for a
 * human.
 *
 * `warn` — emit a warning, skip the gate block, and let the run finish so
 * the PR is still opened. The PR body will note that tests did not pass.
 */
export type GateMode = 'strict' | 'warn';

/** Owner-only write: set the project's gate-blocking mode. */
export interface UpdateProjectGateModeDto {
  gateMode: GateMode;
}

/** The platform's single vocabulary of "risky" — shared by plan-time
 *  auto-release (evaluated against the plan text) and the auto-merge policy
 *  (evaluated against the diff). Same categories, two independent evaluators. */
export type PlanRiskCategory =
  | 'auth'
  | 'schema_migration'
  | 'destructive'
  | 'secrets_infra'
  | 'oversized_scope';

/** Owner-only write: set the project's Team autonomy dial. */
export interface UpdateProjectAutonomyDto {
  autonomy: ProjectAutonomy;
}

/** Outcome of a single pipeline stage within a run. */
export const StageStatus = {
  Pending: 'pending',
  Running: 'running',
  Succeeded: 'succeeded',
  Failed: 'failed',
  Skipped: 'skipped',
  /**
   * Legacy: gate failed and the run paused for a human decision. Strict gate
   * mode now fails the run immediately instead — only older runs (from before
   * that change) can still show this status.
   */
  AwaitingGate: 'awaiting_gate',
} as const;
export type StageStatus = (typeof StageStatus)[keyof typeof StageStatus];

/** AI providers behind the agent abstraction (extensible). */
export const ProviderKind = {
  ClaudeCode: 'claude-code',
} as const;
export type ProviderKind = (typeof ProviderKind)[keyof typeof ProviderKind];

/**
 * Risk classification of the diff produced by a run. Fixed rubric, no config.
 * LOW = safe to auto-merge; HIGH = requires human review.
 */
export const RiskLevel = {
  Low: 'low',
  High: 'high',
} as const;
export type RiskLevel = (typeof RiskLevel)[keyof typeof RiskLevel];

/**
 * Post-run outcome of the auto-merge decision. Set after a succeeded run with
 * an open PR; null if the run never opened a PR or the run didn't succeed.
 */
export const AutoMergeStatus = {
  AutoMerged: 'auto-merged',
  AwaitingReview: 'awaiting-review',
  /** A human merged the held PR from the app (POST /runs/:id/merge). */
  Merged: 'merged',
  /** PR has unresolvable merge conflicts — branch update was attempted but failed. */
  Conflicting: 'conflicting',
} as const;
export type AutoMergeStatus = (typeof AutoMergeStatus)[keyof typeof AutoMergeStatus];

/**
 * One entry in an issue's governance timeline (GET /issues/:id/timeline).
 * The API composes these from the issue's own history, its runs, and stored
 * IssueEvent rows (worker actions with notes, in-app merges, loop filings) so
 * the client renders a single GitHub-style feed without joining anything.
 */
export interface IssueTimelineEventDto {
  id: string;
  /** 'created' | 'filed' | 'run' | 'pr' | 'auto_merge' | 'merged' | 'action' | 'feedback_triaged' | 'feedback_resolved' | 'auto_approved' | 'auto_held' | 'build_retry' | 'build_gave_up' | 'rollback' | 'operator_rerun' | 'title_generated' | 'labels_suggested' | 'design_auto_picked' | 'screened' | 'runtime_error_seen_again' | 'note' */
  type: string;
  /** ISO timestamp of the event. */
  at: string;
  /** Short human-readable line ("Worker marked this issue completed"). */
  title: string;
  /** Optional longer text: the worker's note, hold reason, error message. */
  detail: string | null;
  /** Who acted: "user:<email>" | "run:<id>" | "loop:<name>" | "system". */
  actor: string | null;
  /** Run involved, for linking to the run page. */
  runId: string | null;
  /** External link (e.g. the pull request). */
  url: string | null;
  /** Another app issue this event points at (cross-issue governance link):
   *  on a target issue, the fixer's issue; on the fixer's issue, the target. */
  relatedIssue: { id: string; title: string; code: string | null } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Domain DTOs (what the API returns to the web client)
// ─────────────────────────────────────────────────────────────────────────────

export interface IssueCommentDto {
  id: string;
  issueId: string;
  authorId: string;
  authorEmail: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

/** One conversation in a project's PM chat ("New chat" starts one). */
export interface ChatSessionDto {
  id: string;
  projectId: string;
  /** First user message, truncated — the history-list label. */
  title: string | null;
  /** Email of the member who wrote the session's first message. Null when that author was deleted. */
  startedByEmail?: string | null;
  lastMessageAt: string;
  createdAt: string;
}

/** One message in a project's PM chat, scoped to a session (conversation). */
export interface ChatMessageDto {
  id: string;
  projectId: string;
  sessionId: string;
  role: 'user' | 'pm';
  /** Set on user messages. */
  authorId?: string;
  authorEmail?: string;
  /** Set on pm messages: the chat-lane run that produced the reply. */
  runId?: string;
  body: string;
  createdAt: string;
}

/** Hard cap on a single PM chat reply (runner truncates, API re-caps). */
export const CHAT_REPLY_MAX_LENGTH = 8192;

/** Hard cap on an agent-written visitorTitle (runner truncates, API re-caps). */
export const VISITOR_TITLE_MAX_LENGTH = 100;

/** One entry of chat-reply.json: the PM's single reply for this chat turn. */
export interface ChatReplyEntry {
  body: string;
}

export interface IssueAttachmentDto {
  id: string;
  issueId: string;
  /** ID of the user who uploaded this attachment. Null for system-created attachments. */
  uploadedByUserId: string | null;
  originalFilename: string;
  mimeType: string;
  /** Size in bytes. */
  size: number;
  /** HMAC-signed download URL (no bearer token required). */
  url: string;
  createdAt: string;
}

export interface UserDto {
  id: string;
  email: string;
  createdAt: string;
}

/** An org as returned to the caller who is a member of it — used to let a
 * user discover/pick which org they're acting in. Not org-scoped itself. */
export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'member';
  /** The workspace plan (basic|pro). Drives which create actions the UI offers. */
  plan: OrgPlan;
}

export interface ProjectDto {
  id: string;
  name: string;
  /** The project key — the shared prefix of its issue codes (e.g. "OMHB"), unique
   *  within the org. Also a valid handle for opening the project. Null pre-backfill. */
  code: string | null;
  /** "repo" for a normal project, "prototype" for a hosted prototype. */
  kind: ProjectKind;
  /** e.g. "owner/repo" */
  repoFullName: string;
  repoUrl: string;
  defaultBranch: string;
  /** Whether the global GitHub token (Settings) is configured and usable. */
  hasGithubCredential: boolean;
  /** Whether this is the org's self-project (the target for in-app feedback issues). */
  isSelf: boolean;
  /** How much the always-present team may do without you (Team tab dial). */
  autonomy: ProjectAutonomy;
  /** What happens when tests keep failing after all automatic retries. */
  gateMode: GateMode;
  createdAt: string;
  updatedAt: string;
}

/**
 * Native end-user feedback for a repo (non-prototype) project
 * (`GET/PATCH /projects/:id/feedback`, `POST /projects/:id/feedback/rotate`) —
 * a one-click shortcut over the project's `feedback:write` Integration API
 * token (enable = mint/restore, rotate = rotate, disable = revoke).
 * The user's own server forwards end-user feedback to `feedbackUrl` with the
 * bearer token — the token must never reach browser code.
 */
export interface ProjectFeedbackDto {
  /** Whether the project's `feedback:write` integration token is live. */
  feedbackEnabled: boolean;
  /** First 6 chars of the token followed by "***". Null when disabled. */
  feedbackTokenPreview: string | null;
  /** `<base>/api/integrations/v1/feedback` (AIAI_FEEDBACK_URL). Null when disabled or no public base URL is configured. */
  feedbackUrl: string | null;
  /**
   * The plaintext token — present ONLY on the enable/rotate response that minted
   * it, exactly once. Never returned by GET.
   */
  token?: string;
}

// ── Prototypes ──────────────────────────────────────────────────────────────

export type ProjectKind = 'repo' | 'prototype';

export type PrototypeDeployStatus =
  | 'provisioning'
  | 'building'
  | 'live'
  | 'failed'
  | 'stopped'
  /** Idle for 24 h and not "In production": container stopped, wakes on the next visit. */
  | 'sleeping';

export type PrototypeDomainStatus = 'pending' | 'active' | 'failed';

export interface PrototypeCustomDomainDto {
  id: string;
  domain: string;
  status: PrototypeDomainStatus;
  lastError: string | null;
  lastCheckedAt: string | null;
  createdAt: string;
}

/** A workspace's prototype domain (`*.app.customer.nl`) + the DNS target to point it at. */
export interface WorkspacePrototypeDomainDto {
  domain: PrototypeCustomDomainDto | null;
  /** `edge.<PROTOTYPE_DOMAIN>`; null when the platform has no PROTOTYPE_DOMAIN. */
  cnameTarget: string | null;
  /**
   * Whether each prototype of the workspace is actually serving on the branded
   * host. Empty unless the workspace domain is `active`.
   */
  prototypes: WorkspaceDomainPrototypeDto[];
}

/**
 * Why a prototype is (not) on its branded `<slug>.<workspace domain>` host:
 * `branded` = it is; `pending` = live on the platform URL only, waiting for a
 * relabel ("Re-apply" fixes it); the rest name what blocks the relabel.
 */
export type WorkspaceDomainPrototypeState =
  | 'branded'
  | 'pending'
  | 'deploying'
  | 'failed'
  | 'not_deployed'
  | 'stopped';

export interface WorkspaceDomainPrototypeDto {
  projectId: string;
  name: string;
  slug: string;
  /** The branded URL this prototype should serve on. */
  brandedUrl: string;
  /** The prototype's current primary URL. */
  liveUrl: string;
  state: WorkspaceDomainPrototypeState;
  /** The last deploy/relabel error when `state` is `failed`. */
  detail: string | null;
}

export interface PrototypeDeploymentDto {
  slug: string;
  liveUrl: string;
  /**
   * The platform URL (`https://looproto-<slug>.<PROTOTYPE_DOMAIN>`), always —
   * the CNAME target for per-prototype custom domains. `liveUrl` is the PRIMARY
   * URL and is the branded workspace-domain URL when that is active.
   */
  platformUrl: string;
  deployStatus: PrototypeDeployStatus;
  lastDeployedSha: string | null;
  lastError: string | null;
  updatedAt: string;
  /** Client-owned hostnames routed to this prototype (admin-managed). */
  domains?: PrototypeCustomDomainDto[];
  /**
   * Per-prototype memory cap (MiB). Null means fall back to the global
   * PROTOTYPE_SERVE_MEMORY_MB / PROTOTYPE_SERVE_FULLSTACK_MEMORY_MB config.
   */
  memoryMb: number | null;
  /**
   * Per-prototype CPU limit (fractional vCPU). Null means fall back to the
   * global PROTOTYPE_SERVE_CPU_LIMIT config.
   */
  cpuLimit: number | null;
  /** "In production": never put to sleep when idle. */
  inProduction: boolean;
  /** Last time Traefik counted a request for this prototype (or its last deploy/wake). */
  lastRequestAt: string | null;
}

export interface PrototypeDto {
  id: string;
  name: string;
  /** The project key — the shared prefix of its issue codes (e.g. "OMHB"). Also a
   *  valid handle for opening the prototype. Null pre-backfill. */
  code: string | null;
  description: string;
  repoFullName: string;
  repoUrl: string;
  deployment: PrototypeDeploymentDto;
  /** Whether end-user feedback (the project's `feedback:write` integration token) is enabled for this prototype. */
  feedbackEnabled: boolean;
  /**
   * First 6 chars of the AIAI_FEEDBACK_TOKEN followed by "***" — for operator
   * visibility that the token is set. Null when feedback is disabled.
   */
  feedbackTokenPreview: string | null;
  /**
   * The AIAI_FEEDBACK_URL injected into the serve container. Null when
   * feedback is disabled.
   */
  feedbackUrl: string | null;
  /** Design-style id from PROTOTYPE_DESIGNS (null = default scaffold look). */
  designId: string | null;
  /** Whether per-prototype S3 object storage is provisioned for this prototype. */
  storageEnabled: boolean;
  /**
   * The public S3 endpoint URL (MinIO) when storage is enabled and MinIO is
   * configured; null otherwise. Never contains credentials.
   */
  storageEndpoint: string | null;
  /**
   * Created by a workspace Integration API token (a "factory" prototype).
   * Transferring it detaches it from that token's link and webhooks.
   */
  factory: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Instantaneous resource glance for a live prototype serve container. */
export interface PrototypeStatsDto {
  cpuPercent: number; // whole container, e.g. 4.2
  memUsedMb: number; // working set (usage minus page cache), MiB
  memLimitMb: number; // the container's memory cap, MiB
  uptimeSeconds: number; // seconds since the container started
  restartCount: number; // Docker RestartCount — climbs when the container keeps OOM/crash-restarting
  oomKilled: boolean; // the last exit was an out-of-memory kill (near/over the RAM cap)
}

/**
 * A prototype env entry. A hidden one (the default) is a secret: its value is
 * never returned. A `visible` one is a plain config value the owner marked
 * as not a secret (e.g. the factory setup code): its `value` is returned.
 * Platform-managed keys (`PLATFORM_HIDDEN_SECRET_KEYS`) can never be visible.
 */
export interface PrototypeSecretDto {
  key: string;
  updatedAt: string;
  visible: boolean;
  /** Only present when `visible`. */
  value?: string;
}

/**
 * Platform-managed prototype env keys: never readable, never markable visible.
 * An entry ending in `*` is a prefix (`S3_*` = every `S3_` key).
 */
export const PLATFORM_HIDDEN_SECRET_KEYS = ['JWT_SECRET', 'AIAI_FEEDBACK_TOKEN', 'AIAI_FEEDBACK_URL', 'S3_*'] as const;

/** True for a key in (or under a prefix of) `PLATFORM_HIDDEN_SECRET_KEYS`. */
export function isPlatformHiddenSecretKey(key: string): boolean {
  return PLATFORM_HIDDEN_SECRET_KEYS.some((k) => (k.endsWith('*') ? key.startsWith(k.slice(0, -1)) : key === k));
}

/** The visible PrototypeSecret holding a factory prototype's one-time setup (claim) code. */
export const PROTOTYPE_SETUP_CODE_KEY = 'AIAI_SETUP_CODE';

/**
 * `GET /prototypes/:id/admin-account`: a factory prototype's admin-account claim
 * info (null for app-made or static prototypes). `setupRequired` is the
 * live app's own first-run status (null = not known yet / unreachable).
 */
export type PrototypeAdminDto = import('./integrations').IntegrationPrototypeAdminDto;

// ── Prototype container inspect ───────────────────────────────────────────────
// Read-only snapshot of a live or stopped serve container, analogous to
// `docker inspect` / Portainer's container details view. Secret env var
// values are redacted server-side before reaching the client.

export interface PrototypeInspectMount {
  type: 'bind' | 'tmpfs' | 'volume';
  source: string;
  destination: string;
  readonly: boolean;
}

export interface PrototypeInspectNetwork {
  name: string;
  ip: string;
}

export interface PrototypeInspectEnvVar {
  key: string;
  value: string;
  redacted: boolean;
}

/** Full container introspection for the prototype detail page (admin-style). */
export interface PrototypeInspectDto {
  containerId: string;
  image: string;
  platform: string;
  createdAt: string;
  startedAt: string | null;
  cpuLimit: number;
  memLimitMb: number;
  pidsLimit: number;
  restartPolicy: string;
  capDrop: string[];
  securityOpt: string[];
  readonlyRootfs: boolean;
  networks: PrototypeInspectNetwork[];
  ports: string[];
  mounts: PrototypeInspectMount[];
  env: PrototypeInspectEnvVar[];
  labels: Record<string, string>;
}

// ── Prototype database schema (read-only ERD) ────────────────────────────────
// The API reads the live `server/prisma/schema.prisma` from the prototype repo
// (at the deployed commit) and parses it into this provider-neutral shape, so
// the visualization always mirrors the prototype's actual database — no
// hand-transcription. When the prototype evolves its schema and redeploys, the
// next read reflects it automatically.

export type PrototypeSchemaCardinality = '1:1' | '1:N' | 'N:M';

/** One column (scalar/enum field) of a prototype model. Relation fields are
 *  not columns — they surface as {@link PrototypeSchemaRelationDto} edges. */
export interface PrototypeSchemaColumnDto {
  name: string;
  /** Scalar/enum type as written in the schema, without `?`/`[]` modifiers. */
  type: string;
  /** Part of the model's primary key (`@id` or `@@id`). */
  pk: boolean;
  /** Target model name when this scalar column backs a relation (its FK); else null. */
  fk: string | null;
  /** Field is required (no `?`). */
  required: boolean;
  /** Field is a list (`[]`). */
  list: boolean;
}

export interface PrototypeSchemaModelDto {
  name: string;
  columns: PrototypeSchemaColumnDto[];
}

export interface PrototypeSchemaRelationDto {
  /** Referenced (parent) model — the side holding the primary key. */
  from: string;
  /** Model holding the foreign key (child). */
  to: string;
  cardinality: PrototypeSchemaCardinality;
}

export interface PrototypeSchemaDto {
  /** False when the repo has no `server/prisma/schema.prisma` (e.g. a static prototype). */
  available: boolean;
  /** The git ref/commit the schema was read at (deployed SHA, else default branch). */
  ref: string | null;
  models: PrototypeSchemaModelDto[];
  relations: PrototypeSchemaRelationDto[];
}

/** One parameter of a prototype API operation (path/query/header/cookie). */
export interface PrototypeApiParamDto {
  name: string;
  in: string;
  required: boolean;
  /** Schema type or `$ref` model name, e.g. `string`, `integer[]`, `CreateItemDto`. */
  type?: string;
}

export interface PrototypeApiResponseDto {
  status: string;
  description?: string;
  /** Schema type or `$ref` model name of the (JSON) body. */
  schema?: string;
}

export interface PrototypeApiOperationDto {
  /** Upper-case HTTP method. */
  method: string;
  path: string;
  summary?: string;
  params: PrototypeApiParamDto[];
  /** Schema type or `$ref` model name of the (JSON) request body. */
  requestBody?: string;
  responses: PrototypeApiResponseDto[];
  /** The route declares a security requirement (e.g. bearer JWT). */
  requiresAuth: boolean;
}

export interface PrototypeApiTagDto {
  /** Tag name; untagged operations are grouped under `default`. */
  name: string;
  operations: PrototypeApiOperationDto[];
}

export interface PrototypeApiSchemaPropertyDto {
  name: string;
  type?: string;
  required: boolean;
}

export interface PrototypeApiSchemaDto {
  type?: string;
  properties: PrototypeApiSchemaPropertyDto[];
}

/**
 * Why a prototype's API spec can't be shown: `static` = no full-stack data dir
 * (static prototype / never deployed), `missing` = deployed before the
 * scaffold wrote `openapi.json`, `oversize`/`malformed` = unusable file.
 */
export type PrototypeApiSpecUnavailableReason = 'static' | 'missing' | 'oversize' | 'malformed';

/**
 * Read-only view of a full-stack prototype's API, normalised from the
 * `openapi.json` its deployed container writes to its private data dir.
 */
export type PrototypeApiSpecDto =
  | { available: false; reason: PrototypeApiSpecUnavailableReason }
  | {
      available: true;
      title?: string;
      version?: string;
      tags: PrototypeApiTagDto[];
      schemas: Record<string, PrototypeApiSchemaDto>;
    };

export interface CreatePrototypeRequest {
  title: string;
  description: string;
  /** Optional design-style id from PROTOTYPE_DESIGNS; its prompt is appended
   *  to the first brief. Omitted/null = the scaffold's default look. */
  designId?: string | null;
}

export interface ChangePrototypeDesignRequest {
  /** Design-style id from PROTOTYPE_DESIGNS to restyle the prototype to. */
  designId: string;
}

export interface PrototypePullDto {
  number: number;
  title: string;
  htmlUrl: string;
  headRef: string;
}

export interface IssueDto {
  id: string;
  projectId: string;
  /** Stable human-facing handle, "<PREFIX>-<n>" (e.g. "OMHB-42"). Null only for rows created before the backfill ran. */
  code: string | null;
  /** The owning project's key ("OMHB"), so a page holding an issue can link/fetch the project code-first. Null pre-backfill. */
  projectCode: string | null;
  title: string;
  /** The markdown brief the developer wrote. */
  body: string;
  /** The single lifecycle axis. Replaces the old status+approved fields. */
  state: IssueState;
  /** Why a closed issue was closed. `null` while not closed. */
  resolution?: IssueResolution | null;
  /** Ordered roles to execute. Defaults to the standard loop pipeline. */
  pipeline: AgentRole[];
  /** "local" (app-authored), "github" (mirrors a repo issue), "loop" (created by a scheduled Loop), or "integration" (submitted through the Integration API). */
  source: 'local' | 'github' | 'loop' | 'integration';
  /** GitHub issue number when source === "github", else null. */
  githubNumber: number | null;
  githubUrl: string | null;
  /** ID of the Loop that created this issue (source === "loop"), else null. */
  loopId: string | null;
  /** Name of the Loop that created this issue (source === "loop"), else null. Null also when the loop was deleted. */
  loopName: string | null;
  /** Execution priority: 0 = none, 1 = low, 2 = medium, 3 = high. Higher values are picked first. */
  priority: number;
  /** Free-form topic tags for grouping and filtering (e.g. "backend", "auth"). */
  tags?: string[];
  /** Provenance link: the canonical issue on a `duplicate` close (or, on legacy rows, the feedback item the old PM model planned this from). */
  sourceIssueId?: string | null;
  /** Title of the source feedback issue (populated when sourceIssueId is set). */
  sourceIssueTitle?: string | null;
  /** ID of the parent issue when this issue was created as a child (e.g. from a conversation). */
  parentIssueId?: string | null;
  /** Email of the human who filed it from the app (form, MCP file_issue, in-app feedback). Null for agent/loop/GitHub/integration issues and pre-existing rows. */
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  latestRun?: RunSummaryDto | null;
}

/** Who currently has the ball on an issue — the "assignee" the UI shows. */
export type IssueAssignee = 'planner' | 'builder' | 'you' | 'none';

export interface AssigneeInfo {
  /** planner (📋) · builder (🔧) · you (🧑) · none (done). */
  actor: IssueAssignee;
  /** Short reason, e.g. "planning", "building", "approve to build", "merge". */
  reason: string;
  /** True when the ball is with the human — drives the "needs you" emphasis. */
  needsYou: boolean;
}

/**
 * Derive who an issue is assigned to, from its lifecycle state + latest run.
 * The single source of truth for the assignee avatar on the board and the issue
 * page: agents (planner/builder) while they work, YOU at the exception points
 * (approve a held plan, merge, retry a failure). Pure — needs nothing beyond
 * IssueDto — so API and web render it identically.
 */
export function assigneeFor(
  issue: Pick<IssueDto, 'state' | 'latestRun' | 'tags'>,
  opts?: {
    /** Project autonomy dial. Drives two branches:
     *  - `open` issues: Manual → You ("run"), others → Builder ("queued").
     *  - `proposed` issues: Manual (or undefined) → You ("approve to build");
     *    autopilot splits further by plan tag and latest-run lane.
     *  `undefined` = unknown → behaves like historical default (auto-builds). */
    autonomy?: ProjectAutonomy;
  },
): AssigneeInfo {
  const run = issue.latestRun ?? null;
  const runActive = !!run && !TERMINAL_RUN_STATUSES.includes(run.status);

  // When a run is actively going, name the agent from its LANE (authoritative) —
  // a Planner can run on an open issue (a revisit), and a build only ever runs in
  // the build lane. Guessing from issue state mislabels those. Falls through to
  // the state heuristics when lane is absent (legacy rows / loading).
  if (runActive && run) {
    if (run.lane === 'plan') return { actor: 'planner', reason: 'planning', needsYou: false };
    if (run.lane === 'build') return { actor: 'builder', reason: 'building', needsYou: false };
  }

  switch (issue.state) {
    case IssueState.Closed:
      return { actor: 'none', reason: 'done', needsYou: false };
    case IssueState.Failed:
      return { actor: 'you', reason: 'needs attention', needsYou: true };
    case IssueState.Review:
      return { actor: 'you', reason: 'merge', needsYou: true };
    case IssueState.Building:
      return { actor: 'builder', reason: 'building', needsYou: false };
    case IssueState.Open:
      // Approved & buildable. A build already running → Builder. Otherwise it
      // depends on whether the project auto-builds: on a Manual-autonomy
      // project nothing will pick it up — the ball is with you to Run it.
      if (runActive) return { actor: 'builder', reason: 'building', needsYou: false };
      if (opts?.autonomy === 'manual') return { actor: 'you', reason: 'run', needsYou: true };
      return { actor: 'builder', reason: 'queued to build', needsYou: false };
    case IssueState.Proposed:
    default: {
      // planned tag → human must approve the PM's plan.
      if (issue.tags?.includes('planned')) {
        return { actor: 'you', reason: 'approve the plan', needsYou: true };
      }
      const autonomy = opts?.autonomy;
      // Manual or unknown → direct approve-to-build (no PM in the loop).
      if (!autonomy || autonomy === 'manual') {
        return { actor: 'you', reason: 'approve to build', needsYou: true };
      }
      // autopilot: PM had a settled plan-lane run without producing a
      // plan tag → human needs to reply or close.
      if (run?.lane === 'plan') {
        return { actor: 'you', reason: 'reply or close', needsYou: true };
      }
      // No PM turn yet → planner will pick it up reactively.
      return { actor: 'planner', reason: 'queued to plan', needsYou: false };
    }
  }
}

export type IssueBoardState =
  | 'all' | 'proposed' | 'open' | 'building' | 'review' | 'failed' | 'closed'
  /** "Needs you" — the assignee is you: a held/proposed plan, a PR to merge, or a
   *  failed run. The board's to-do queue. */
  | 'mine';

/** Default board page size (server + web must agree). */
export const ISSUE_BOARD_PAGE_SIZE = 25;

export interface PaginatedIssuesDto {
  /** One page, ordered by state (needs-you first) then recency. */
  items: IssueDto[];
  /** Count matching the active filter (drives totalPages). */
  total: number;
  /** Project-wide per-state counts, ignoring tag/search — the tab labels. */
  counts: {
    all: number; proposed: number; open: number;
    building: number; review: number; failed: number; closed: number;
    /** "Needs you": proposed + review + failed. */
    mine: number;
  };
  tags: string[];
}

/** A GitHub repo issue (live), used to browse and pull issues into the loop. */
export interface GithubIssueDto {
  number: number;
  title: string;
  body: string;
  state: 'open' | 'closed';
  htmlUrl: string;
  labels: string[];
  updatedAt: string;
  /** True if a local mirror already exists for this issue in this project. */
  pulledIn: boolean;
}

export interface RunSummaryDto {
  id: string;
  /** Null for issue-less (chat) runs. */
  issueId: string | null;
  /** Null once the project was deleted (the run is kept for metrics). */
  projectId: string | null;
  status: RunStatus;
  /** Concurrency lane: 'plan' (a Planner run), 'build' (a Builder run), or
   *  'chat' (a project PM-chat turn). Lets the UI name the agent actually
   *  working, rather than guessing from state. */
  lane: 'plan' | 'build' | 'chat';
  branch: string | null;
  pullRequestUrl: string | null;
  /** Last-known state of `pullRequestUrl` on GitHub (`none` if no PR). */
  pullRequestState: PullRequestState;
  /** When `pullRequestState` was last refreshed from GitHub, if ever. */
  pullRequestSyncedAt: string | null;
  /** The run this one iterates on (continues its branch), if any. */
  parentRunId: string | null;
  /** Risk classification of the diff: null if not yet assessed. */
  riskLevel: RiskLevel | null;
  /** Human-readable reason this risk level was assigned. */
  riskJustification: string | null;
  /** Post-run merge outcome: null if no PR, or run didn't succeed. */
  autoMergeStatus: AutoMergeStatus | null;
  /** Reason the PR was held for review (set when autoMergeStatus = "awaiting-review"). */
  autoMergeReason: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  /** Total cost in USD across all stages. Null for in-progress or uncosted runs. */
  totalCostUsd: number | null;
  /**
   * Why a `failed` / `timed_out` run ended, one of `RUN_FAILURE_REASONS`
   * (null on any other status, and on runs finalized before the reason was
   * classified). Optional so existing frontend fixtures without the field
   * remain valid; the backend always sends it.
   */
  failureReason?: string | null;
}

/**
 * Every `Run.failureReason` value, most specific first. `cost_cap` = the
 * per-run maxRunCostUsd was exceeded; `monthly_cap` = the workspace's monthly
 * allowance ran out; `repo_unreachable` = GitHub answered "Repository not
 * found"; `timeout` = the run exceeded AGENT_TIMEOUT_SECONDS; `oom` = the
 * container hit its memory limit and the agent was killed; `usage_limit` = the
 * Claude subscription's usage limit; `gate` = the test gate still failed when
 * the loop budget ran out; `push_failed` = work committed but git push failed;
 * `orphaned` = finalization never ran and the reaper closed the run;
 * `agent_error` = anything else the agent reported.
 */
export const RUN_FAILURE_REASONS = [
  'cost_cap',
  'monthly_cap',
  'repo_unreachable',
  'timeout',
  'oom',
  'usage_limit',
  'gate',
  'push_failed',
  'orphaned',
  'agent_error',
] as const;
export type RunFailureReason = (typeof RUN_FAILURE_REASONS)[number];

/** One metric that regressed compared to the project's rolling baseline. */
export interface ScorecardRegression {
  /** Machine-readable metric key, e.g. 'cost', 'loopIterations', 'linesChanged'. */
  metric: string;
  /** Human-readable label for UI display. */
  label: string;
  /** This run's value. */
  value: number;
  /** Rolling average of the last N baseline runs. */
  baseline: number;
  /** value / baseline, rounded to one decimal. */
  multiplier: number;
  /** 'warn' = 2–4× baseline; 'alert' = >4× baseline. */
  severity: 'warn' | 'alert';
}

/** Per-run quality scorecard, computed at finalization and persisted on the run. */
export interface RunScorecard {
  /** Whether the final gate passed (run succeeded). */
  gatePassed: boolean;
  /** How many gate iterations were used (1 = first attempt succeeded). */
  loopIterations: number;
  /** Number of files changed. */
  filesChanged: number;
  /** Lines added in the diff. */
  linesAdded: number;
  /** Lines removed in the diff. */
  linesRemoved: number;
  /** Lines added to test files (`.spec`, `.test`, `_test`, `/tests/`). */
  testsAddedCount: number;
  /** Total cost in USD across all stages. */
  totalCostUsd: number | null;
  /** Wall-clock duration in seconds (finishedAt − startedAt). */
  durationSeconds: number | null;
  /** Total tokens used across all stages (input + output + cache). */
  totalTokens: number | null;
  /** Regressions vs the project's rolling baseline (empty when no baseline). */
  regressions: ScorecardRegression[];
}

export interface RunDto extends RunSummaryDto {
  /** The run's issue short code ("OMHB-42"), for display + code-first links. Null pre-backfill. */
  issueCode: string | null;
  /** The run's project key ("OMHB"), so the run page links/fetches the project code-first. Null pre-backfill. */
  projectCode: string | null;
  pipeline: AgentRole[];
  containerId: string | null;
  errorMessage: string | null;
  /** Human feedback that seeded this iteration, if it is one. */
  feedback: string | null;
  stages: StageDto[];
  /** Files the agent created/modified on the work branch. */
  changedFiles: ChangedFile[];
  /** Unified diff (base..HEAD), capped. Null until available. */
  diff: string | null;
  /** Quality scorecard computed at finalization. Null for in-progress runs. */
  scorecard: RunScorecard | null;
  /** Peak container memory usage (MiB) during the run. Null when unavailable. */
  peakMemoryMb: number | null;
  /** Minimum host MemAvailable (MiB) while the container ran. Null on non-Linux hosts. */
  minHostFreeMb: number | null;
  /** True when the Linux OOM killer terminated the container. */
  oomKilled: boolean | null;
  /**
   * Git SHA of the pushed commit, null when the branch was never pushed to the
   * remote (e.g. issue-only worker runs). Used to gate iterate availability:
   * iterating on an unpushed branch fails immediately in the runner.
   */
  commitSha: string | null;
  /** Total cost in USD across all stages. Available for live runs and after finalization. */
  totalCostUsd: number | null;
  /** Runs that were created as iterations of this one (parentRunId = this run's id). */
  childRuns: RunSummaryDto[];
  /**
   * The per-run cost cap (USD) that applies to this run: its one-off
   * `costCapUsdOverride` when an owner raised it on Run again, else the
   * workspace's current `maxRunCostUsd`. Null = no cap. Optional so older
   * fixtures stay valid; the backend always sends it on run detail.
   */
  costCapUsd?: number | null;
  /** The workspace's current per-run cost cap (USD), null = none. Bounds a
   *  Run again override (≤ `MAX_COST_CAP_OVERRIDE_MULTIPLIER` × this). */
  orgMaxRunCostUsd?: number | null;
}

/**
 * A currently-active run (non-terminal), denormalized for the live agents
 * overview so the page can render each row without extra lookups.
 */
export interface ActiveRunDto {
  /** Newest tool call of the run (hub astronauts + live gate light); absent when unknown. */
  activity?: HubActivityDto | null;
  id: string;
  projectId: string;
  /** Null for issue-less (chat) runs. */
  issueId: string | null;
  /** The issue's short code ("OMHB-42"), for display + pretty links. Null pre-backfill. */
  issueCode: string | null;
  repoFullName: string;
  /** Empty for issue-less (chat) runs. */
  issueTitle: string;
  status: RunStatus;
  /**
   * Concurrency lane: 'plan', 'build', or 'chat'. Optional so existing frontend
   * fixtures without the field remain valid; the backend always sends it. Lets
   * the Chat page seed its "PM is thinking" state from an in-flight chat-lane run.
   */
  lane?: 'plan' | 'build' | 'chat';
  /** For chat-lane runs: the conversation being answered. Null otherwise. */
  chatSessionId?: string | null;
  /** True when any stage is in the legacy `awaiting_gate` status (older runs only — new runs fail immediately instead). */
  awaitingGate: boolean;
  /** Role of the stage currently running (or the furthest reached); null if none. */
  currentStageRole: AgentRole | null;
  /** 1-based order of that stage; 0 before any stage starts. */
  currentStageOrder: number;
  totalStages: number;
  /** Count of gate failures that have triggered a loop retry so far. 0 on first attempt. */
  loopCount: number;
  totalCostUsd: number | null;
  containerId: string | null;
  startedAt: string | null;
  createdAt: string;
  pullRequestUrl: string | null;
  pullRequestState: PullRequestState;
  /** Most recent non-empty log line from the run (null if none yet). Used for the live ticker on the Overview. */
  lastLogLine: string | null;
}

/** Result of the "stop all running agents" kill switch. */
export interface StopAllRunsResponse {
  stopped: number;
}

/** Outcome of restoring an uploaded backup archive (admin only). */
export interface RestoreBackupResponse {
  restored: boolean;
  /** The restored DB is only picked up after an API restart. */
  requiresRestart: boolean;
  /**
   * True when the API scheduled its own restart to load the restored data
   * (the container runs with restart:unless-stopped). The client should wait
   * for the API to come back, then reload.
   */
  restarting?: boolean;
  /**
   * Whether a stored secret decrypted under this installation's
   * SECRETS_MASTER_KEY. False ⇒ the archive came from an installation with a
   * different key; data restored but credentials must be re-entered.
   */
  secretsDecryptable: boolean;
  /** Human-readable caveat shown to the operator (e.g. a key mismatch). */
  warning?: string;
}

/** How many stored backups the ops plane keeps (newest first; older ones are pruned). */
export const BACKUP_KEEP = 7;

/** Who started a stored backup: the nightly 00:00 job or an operator ("Back up now"). */
export type StoredBackupKind = 'scheduled' | 'manual';

/** One archive in BACKUP_STORAGE_ROOT. `at` is the file's mtime (ISO). */
export interface StoredBackupDto {
  name: string;
  at: string;
  kind: StoredBackupKind;
  sizeBytes: number;
}

/** GET /api/admin/backups (ops plane). */
export interface StoredBackupsResponse {
  /** Valid backups, newest first. */
  items: StoredBackupDto[];
  /** The backup being written right now, if any. */
  running: { kind: StoredBackupKind; startedAt: string } | null;
  /** The last failed attempt, cleared by the next success. */
  lastFailure: { at: string; error: string } | null;
  keep: number;
}

/** POST /api/admin/backups — the backup runs in the background. */
export interface StartBackupResponse {
  started: true;
}

/** A recent (terminal) run, denormalized with issue title and repo for the activity feed. */
export interface RecentRunDto extends RunSummaryDto {
  issueTitle: string;
  /** The issue's short code ("OMHB-42"), for display + pretty links. Null pre-backfill. */
  issueCode: string | null;
  repoFullName: string;
  /** Number of gate-failure retries (0 on first-attempt success). */
  loopCount: number | null;
  /** Quality scorecard computed at finalization. Null for in-progress or unscored runs. */
  scorecard: RunScorecard | null;
}

/** A recently-updated issue for the cross-workspace activity feed on the dashboard. */
/** One recently-updated issue row within a RecentActivityGroupDto. */
export interface RecentIssueDto {
  id: string;
  code: string | null;
  title: string;
  state: IssueState;
  resolution?: IssueResolution | null;
  updatedAt: string;
}

/**
 * The recent activity of one project/prototype: the group header (name/kind/
 * links) plus its most-recently-updated issues, newest first. The dashboard
 * feed is a list of these — the last N projects touched, capped per project.
 */
export interface RecentActivityGroupDto {
  projectId: string;
  projectName: string;
  /** Pretty project code (e.g. "OMHB"), for the project-page link; null pre-backfill. */
  projectCode: string | null;
  /** Repo vs prototype — picks the project route and the header badge. */
  projectKind: ProjectKind;
  /** Slug + display name of the workspace, for the header link and label. */
  workspaceSlug: string;
  workspaceName: string;
  /** Most recent issue update in the group (ISO), for ordering groups by recency. */
  updatedAt: string;
  issues: RecentIssueDto[];
}

/** One matching log line returned by the cross-run log search endpoint. */
export interface LogSearchHit {
  runId: string;
  /** Null for issue-less (chat) runs. */
  issueId: string | null;
  /** Null once the project was deleted. */
  projectId: string | null;
  issueTitle: string;
  repoFullName: string;
  runStatus: RunStatus;
  runCreatedAt: string;
  logSeq: number;
  logTs: string;
  logRole: string | null;
  logText: string;
}

export interface ChangedFile {
  path: string;
  status: 'added' | 'modified' | 'deleted' | 'renamed' | 'copied' | 'changed';
  additions: number | null;
  deletions: number | null;
}

export interface StageDto {
  id: string;
  runId: string;
  role: AgentRole;
  /** 1-based order within the pipeline (loops reuse roles with higher index). */
  order: number;
  status: StageStatus;
  costUsd: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** ISO timestamp of the last status change; used to show elapsed wait time in 'awaiting_gate'. */
  updatedAt: string | null;
  summary: string | null;
  /** Raw gate failure output when status is 'awaiting_gate' or 'failed' via gate. */
  gateOutput?: string | null;
  /** Token usage for this stage (from the Claude CLI result event). */
  inputTokens?: number | null;
  outputTokens?: number | null;
  cacheCreationTokens?: number | null;
  cacheReadTokens?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Observability read model (API → web client) — the per-stage loop view.
// StageAttempt = one execution of a stage (loop iterations preserved), reduced
// to what the run-detail pipeline renders: the gate outcome + re-loop reason.
// ─────────────────────────────────────────────────────────────────────────────

export interface StageAttemptDto {
  id: string;
  runId: string;
  order: number;
  /** 0-based loop iteration this attempt belongs to. */
  iteration: number;
  gatePassed: boolean | null;
  /** Why the loop restarted after this attempt failed its gate. Null on first-attempt successes. */
  loopReason: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Real-time events (WebSocket: API → web client)
// ─────────────────────────────────────────────────────────────────────────────

export const RunEventType = {
  /** Raw stdout/stderr line from the container or runner. */
  Log: 'log',
  /** A pipeline stage changed status. */
  Stage: 'stage',
  /** The run itself changed status. */
  Status: 'status',
  /** A structured event parsed from Claude Code stream-json. */
  AgentEvent: 'agent-event',
} as const;
export type RunEventType = (typeof RunEventType)[keyof typeof RunEventType];

export interface LogLine {
  ts: string;
  /** 'stdout' | 'stderr' | 'system' */
  stream: 'stdout' | 'stderr' | 'system';
  role?: AgentRole;
  text: string;
  /** Structured timeline kind (absent on legacy lines). */
  kind?: LogEntryKind;
  /** Structured extras (absent on legacy lines / kinds without extras). */
  meta?: LogEntryMeta;
}

export interface RunEvent {
  type: RunEventType;
  runId: string;
  seq: number;
  payload: LogLine | RunSummaryDto | StageDto | Record<string, unknown>;
}

/**
 * One rendered line from a prototype serve container's stdout/stderr, streamed
 * live over the `/prototype-logs` socket namespace. `ts` is Docker's per-line
 * RFC3339 timestamp when present, else null.
 */
export interface PrototypeLogLine {
  ts: string | null;
  stream: 'stdout' | 'stderr';
  text: string;
  /**
   * Where the line came from: the image build / deploy pipeline (`build`) or
   * the running serve container (`runtime`). Absent = runtime.
   */
  source?: 'build' | 'runtime';
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent runtime job contract (API → container, via mounted job.json + env)
// This is the boundary that keeps the platform AI-agnostic.
// ─────────────────────────────────────────────────────────────────────────────

export interface AgentJobSpec {
  schemaVersion: 1;
  runId: string;
  provider: ProviderKind;
  repo: {
    /** HTTPS clone URL, no embedded credentials. */
    url: string;
    fullName: string;
    baseBranch: string;
    /** Branch the agent creates and pushes to. */
    workBranch: string;
  };
  issue: {
    title: string;
    /** The markdown brief. */
    body: string;
  };
  /**
   * Repo-relative directory where loop agents write their structured docs
   * (e.g. "docs/aiai/<slug>-<runId>"). Prompts reference it via {{DOCS_DIR}};
   * the runtime substitutes this value before invoking the agent.
   */
  docsDir: string;
  pipeline: PipelineStageSpec[];
  /** Whether to open a PR at the end. */
  openPullRequest: boolean;
  /**
   * Snapshot of the project's open issues (the "board"), handed to a worker so
   * it can read its to-do list and manage tasks without any live app access.
   */
  board?: BoardIssue[];
  /**
   * Present when this run is a human-in-the-loop iteration: instead of branching
   * from base, the runner continues the existing `fromBranch` and injects
   * `feedback` into every stage prompt. `repo.workBranch` equals `fromBranch`,
   * so follow-up commits push to the same branch (updating its PR).
   *
   * `fromBranch` is absent when the parent's PR merged or was closed/rolled
   * back: merged work lives in the base branch now, and a closed PR's branch
   * is abandoned, so the run branches fresh from base while the feedback is
   * still injected (continuing a merged branch would open a second,
   * conflicting PR from it; continuing a closed one would rebuild on stale
   * work).
   */
  iterate?: {
    feedback: string;
    fromBranch?: string;
  };
  limits: {
    /** Hard wall-clock cap for the whole run; the container is killed past it. */
    timeoutSeconds: number;
    /**
     * How often (in assistant turns) a still-running stage triggers a
     * "long-running" notification, e.g. 50 → notify at 50, 100, 150, … turns.
     * There is no cap on turns themselves — a stage runs until it finishes or
     * errors.
     */
    highTurnsThreshold: number;
    /**
     * How often (in seconds) a still-running stage triggers a "taking a
     * while" notification, e.g. 2400 → notify at 2400s, 4800s, 7200s, … of
     * wall-clock time. This does NOT kill the stage — a long-running task is
     * expected and legitimate; it only re-fires the notification. The
     * whole-run `timeoutSeconds` above remains the only hard cap.
     */
    stageTimeoutSeconds: number;
  };
  /**
   * Public URL of the web app (e.g. "https://aiai.example.com"). Used by the
   * runner to embed clickable run links in commit messages and PR bodies.
   * Optional — links are omitted when absent.
   */
  appUrl?: string;
  /**
   * Persistent per-project knowledge accumulated across runs. The runner
   * injects this into every stage prompt so agents build on prior lessons.
   * Null when no memory has been written yet for this project.
   */
  memory?: string | null;
  /**
   * Auto-generated, self-maintaining architecture map of the project. Agents
   * read it at run start to orient themselves quickly, and update it when they
   * learn something structural about the codebase. Null when no wiki has been
   * written yet for this project.
   */
  wiki?: string | null;
  /**
   * Gate failure output from the most recent prior failed run of this issue.
   * Injected into the first attempt's prompt so the agent targets the root cause
   * instead of repeating the same approach. Absent on first-ever runs or when
   * no prior run ended with a gate failure. Truncated to the last 2 000 chars.
   */
  priorGateFailure?: {
    /** ID of the run that failed the gate. */
    runId: string;
    /** Pipeline stage role that failed (e.g. "worker"). */
    role: string;
    /** Truncated gate command output. */
    output: string;
  };
  /**
   * What the runner does when automatic retries are exhausted and tests still
   * fail. `strict` (default) fails the run; `warn` emits a warning
   * and continues so a PR is opened anyway.
   */
  gateMode?: GateMode;
  /**
   * Maximum number of automatic retry loops before the gate action fires.
   * Defaults to 5 when absent. Must be ≥ 1.
   */
  maxLoops?: number;
  /**
   * Set only for build runs of an Integration API factory prototype: the
   * Engineer also writes a one-sentence, visitor-facing `releaseNote` in
   * /aiai/pr-meta.json, in `locale` (null = the brief's language).
   */
  releaseNote?: { locale: 'nl' | 'en' | null };
  /**
   * Set only for Engineer and Product Manager runs of an Integration API
   * factory prototype: issues filed in /aiai/issues.json and PM plan comments
   * in /aiai/issue-comments.json may carry a visitor-facing `visitorTitle`, in
   * `locale` (null = the brief's language), shown in the prototype's `upcoming`.
   */
  visitorTitle?: { locale: 'nl' | 'en' | null };
}

/** Placeholder in a role's system prompt, replaced with the run's docsDir. */
export const DOCS_DIR_PLACEHOLDER = '{{DOCS_DIR}}';

/** Placeholder in a role's prompt/gate command, replaced with the repo's base branch. */
export const BASE_BRANCH_PLACEHOLDER = '{{BASE_BRANCH}}';

export interface PipelineStageSpec {
  role: AgentRole;
  /** System prompt appended to the agent for this role. */
  systemPrompt: string;
  /** Optional allowed-tools restriction passed to Claude Code. */
  allowedTools?: string[];
  /** Stage exits non-zero if this command (run in repo) fails; enables loop. */
  gateCommand?: string;
  /**
   * Per-role override for the "taking a while" notification cadence (seconds).
   * Falls back to `limits.stageTimeoutSeconds` when unset, so a heavy role
   * (autonomous) and a trivial one (reporter) can be tuned independently
   * instead of sharing one global value.
   */
  stageTimeoutSeconds?: number;
}

/**
 * Final structured result. The runner emits it as the terminal `result` control
 * event on events.jsonl, the authoritative channel the API finalizes from.
 */
export interface AgentRunResult {
  runId: string;
  status: Extract<RunStatus, 'succeeded' | 'failed' | 'timed_out'>;
  branch: string | null;
  commitSha: string | null;
  pullRequestUrl: string | null;
  /**
   * Set when the run pushed a branch but could NOT open its pull request
   * (GitHub outage, missing pull_requests:write). `branch` and `commitSha` are
   * valid — the work is delivered to GitHub, only the PR is missing. Drives the
   * API-side recovery sweep, which opens the PR later over REST.
   */
  pullRequestError?: string | null;
  totalCostUsd: number | null;
  stages: Array<{
    role: AgentRole;
    order: number;
    status: StageStatus;
    costUsd: number | null;
    summary: string | null;
  }>;
  changedFiles?: ChangedFile[];
  diff?: string | null;
  /**
   * The Engineer's visitor-facing line from /aiai/pr-meta.json `releaseNote`
   * (only asked for on factory-prototype builds; see AgentJobSpec.releaseNote).
   */
  releaseNote?: string | null;
  errorMessage: string | null;
  /**
   * Machine-readable failure class, set when the runner recognised a
   * deterministic failure. `repo_unreachable` = clone/push answered
   * "Repository not found" (repo deleted/moved, or the credential cannot see
   * it) — the API never retries it and rewrites the message per credential rail.
   * `oom` = the sandbox hit its memory cap (a gate exited 137, or claude was
   * SIGKILLed) — the API never retries it either: the same work would be
   * killed again.
   */
  failureKind?: 'repo_unreachable' | 'oom';
}

// ─────────────────────────────────────────────────────────────────────────────
// API request payloads
// ─────────────────────────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
  /** Account slot (0–4) the session lives in — multi-account browsers. */
  slot?: number;
}
export interface LoginResponse {
  accessToken: string;
  user: UserDto;
  /** Account slot (0–4) the session lives in — multi-account browsers. */
  slot?: number;
}

/**
 * Result of the first login step. When the account has TOTP 2FA enabled the
 * password step yields only a short-lived `mfaToken` to exchange for a session
 * via the TOTP step; otherwise the session is returned directly.
 */
export interface LoginResult {
  requiresTotp: boolean;
  /** Present when requiresTotp: exchange with a TOTP/recovery code. */
  mfaToken?: string;
  /** Present when !requiresTotp. */
  accessToken?: string;
  user?: UserDto;
  /** Account slot (0–4) the session lives in — multi-account browsers. */
  slot?: number;
}

export interface LoginTotpRequest {
  mfaToken: string;
  /** 6-digit TOTP code or a one-time recovery code. */
  code: string;
  /** Account slot (0–4) the session lives in — multi-account browsers. */
  slot?: number;
}

/** How many accounts one browser can be signed in to at once (Gmail-style `/u/N`). */
export const MAX_ACCOUNT_SLOTS = 5;

/** A signed-in account in this browser, from `GET /auth/accounts`. Identity only — never a token. */
export interface AccountDto {
  slot: number;
  userId: string;
  email: string;
  /** Present only when the request asked about a workspace: is this account a member of it? */
  hasWorkspace?: boolean;
}

/** Two-factor status for the current account. */
export interface TwoFactorStatusDto {
  enabled: boolean;
  /** Whether the deployment requires 2FA (enforced after login). */
  required: boolean;
}

/** Returned when starting TOTP enrollment. */
export interface TwoFactorSetupDto {
  /** otpauth:// URI to add to an authenticator app. */
  otpauthUrl: string;
  /** PNG data URL of the QR code for otpauthUrl. */
  qrDataUrl: string;
  /** Base32 secret, for manual entry. */
  secret: string;
}

export interface Enable2faRequest {
  code: string;
}
/** Returned once on enable — store these somewhere safe. */
export interface RecoveryCodesDto {
  recoveryCodes: string[];
}
export interface Disable2faRequest {
  password: string;
  code: string;
}

/** A repository the configured GitHub token can access (for the repo picker). */
export interface GithubRepoDto {
  fullName: string; // "owner/repo"
  name: string;
  owner: string;
  private: boolean;
  defaultBranch: string;
  htmlUrl: string;
  description: string | null;
  updatedAt: string | null;
  /** Whether the configured token can PUSH (write) — needed for branches/PRs. */
  canPush: boolean;
}

export interface CreateProjectRequest {
  /** owner/repo or a github.com URL. Name + branch are derived from GitHub. */
  repoUrl: string;
}

export interface CreateIssueRequest {
  projectId: string;
  title?: string;
  body: string;
  pipeline?: AgentRole[];
  /** Execution priority: 0 = none (default), 1 = low, 2 = medium, 3 = high. */
  priority?: number;
  /** Free-form topic tags for grouping/filtering. */
  tags?: string[];
  /** True once the issue is buildable/ready-to-build; false while it still needs approval. */
  approved?: boolean;
}

export interface UpdateIssueRequest {
  title?: string;
  body?: string;
  pipeline?: AgentRole[];
  status?: IssueStatus;
  /** True once the issue is buildable/ready-to-build; false while it still needs approval. */
  approved?: boolean;
  /** Execution priority: 0 = none (default), 1 = low, 2 = medium, 3 = high. */
  priority?: number;
  /** Free-form topic tags for grouping/filtering. */
  tags?: string[];
}

/** Create a GitHub issue from the app. */
export interface CreateGithubIssueRequest {
  title: string;
  body: string;
  labels?: string[];
}

/** Manually close an issue (non-destructive alternative to delete). */
export interface CloseIssueRequest {
  resolution: IssueResolution;
  note?: string;
  /** When closing as a duplicate/planned-into, the ID of the related issue. */
  sourceIssueId?: string;
}

/** Continue a finished run's branch with human feedback injected. */
export interface IterateRunRequest {
  /** What the agents should address on the next pass. */
  feedback: string;
}

/** One entry in the workspace file tree (GET /runs/:id/workspace). */
export interface WorkspaceEntryDto {
  /** Repo-relative path, forward-slash separated. */
  path: string;
  type: 'file' | 'dir';
  /** Size in bytes; present for files only. */
  size?: number;
}

/** File content from GET /runs/:id/workspace/file?path=. */
export interface WorkspaceFileDto {
  /** Repo-relative path. */
  path: string;
  /** UTF-8 content, capped at 500 KB. */
  content: string;
  /** True when content was capped at the limit. */
  truncated: boolean;
}

/** Update a project's settings (extends existing UpdateProjectRequest). */
export interface UpdateProjectRequest {
  name?: string;
  defaultBranch?: string;
  issueLabelFilter?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Metrics (operator dashboard)
// ─────────────────────────────────────────────────────────────────────────────

/** Aggregate stats for one agent role across the window. */
export interface RoleMetric {
  role: string;
  /** Completed stage executions (loops count each execution separately). */
  count: number;
  /** Average wall-clock seconds per stage execution (null if unknown). */
  avgDurationSeconds: number | null;
  /** Average USD cost attributed to the stage (null if unknown). */
  avgCostUsd: number | null;
}

/** Per-project cost + run breakdown within the metrics window. */
export interface ProjectMetric {
  /** Null for a deleted project: its runs still count, under its last name. */
  projectId: string | null;
  /** The project key ("OMHB") for pretty links; null pre-backfill. */
  projectCode: string | null;
  projectName: string;
  projectKind: ProjectKind;
  runCount: number;
  totalCostUsd: number;
}

/** Per-loop cost + run breakdown within the metrics window. */
export interface LoopMetric {
  loopId: string;
  projectId: string;
  /** The project key ("OMHB") for pretty links; null pre-backfill. */
  projectCode: string | null;
  loopName: string;
  runCount: number;
  totalCostUsd: number;
}

/** Aggregate visit count for a single screen within the metrics window. */
export interface ScreenVisitDto {
  screen: string;
  count: number;
}

/** Aggregate click count for a single UI element+screen pair within the metrics window. */
export interface InteractionEventDto {
  element: string;
  screen: string;
  count: number;
}

/** One day in the metrics time series. */
export interface MetricsDayPoint {
  /** ISO date (YYYY-MM-DD, UTC) of the bucket start (the Monday for weekly buckets). */
  date: string;
  runs: number;
  succeeded: number;
  costUsd: number;
}

/** Aggregate token usage across all stages in a metrics window. */
export interface TokenUsageDto {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
}

/** Platform health + cost overview over a trailing window. */
export interface MetricsDto {
  /** Span of the metrics range, in days. */
  windowDays: number;
  /** Start of the range (ISO). For an "all time" view, the earliest run (or now). */
  rangeFrom: string;
  /** End of the range (ISO), inclusive. */
  rangeTo: string;
  /** Granularity of `series`: one point per UTC day, or per ISO week (Monday, UTC). */
  seriesBucket: 'day' | 'week';
  totalRuns: number;
  /** Count of runs per RunStatus in the window. */
  byStatus: Record<string, number>;
  /** succeeded / (terminal runs); 0..1. Null when there are no terminal runs. */
  successRate: number | null;
  totalCostUsd: number;
  avgCostUsd: number | null;
  /** Average wall-clock seconds for terminal runs with timing (null if none). */
  avgDurationSeconds: number | null;
  /**
   * Average number of gate-loop iterations per run (null when no attempt data).
   * 1.0 = every run passed its gate on the first try; >1 means re-runs occurred.
   */
  avgLoopIterations: number | null;
  /** Total gate attempts that failed (gatePassed = false) in the window. */
  totalGateFailures: number;
  /**
   * Fraction of runs that had at least one gate failure (0..1).
   * Null when there are no runs with attempt data.
   */
  gateFailureRate: number | null;
  roles: RoleMetric[];
  series: MetricsDayPoint[];
  /** Per-project cost + run breakdown, sorted by totalCostUsd desc. */
  byProject: ProjectMetric[];
  /** Per-loop cost + run breakdown, sorted by totalCostUsd desc. Only populated for runs filed by a scheduled loop. */
  byLoop: LoopMetric[];
  /** Number of runs in the window that produced a merged pull request. */
  mergedPrCount: number;
  /**
   * Average total cost across ALL runs in the window divided by the number of
   * merged PRs — a fleet-level efficiency signal.  Null when no PRs were merged.
   */
  costPerMergedPr: number | null;
  /** Aggregate token usage across all stages in the window. Null when no stage has token data. */
  tokenUsage: TokenUsageDto | null;
}

/**
 * Platform-wide UI usage analytics over a trailing window. Separate from
 * MetricsDto because these events carry no workspace scope — they are
 * cross-tenant operator data surfaced only on the admin panel, never on the
 * per-workspace /metrics page.
 */
export interface AnalyticsDto {
  /** Size of the trailing window, in days. */
  windowDays: number;
  /** Per-screen visit counts over the trailing window, sorted by count desc. */
  screenVisits: ScreenVisitDto[];
  /** Per-element interaction counts over the trailing window, sorted by count desc. */
  interactions: InteractionEventDto[];
}

/** Body for POST /metrics/screen-visit. */
export interface RecordScreenVisitRequest {
  screen: string;
}

/** Body for POST /metrics/interaction. */
export interface RecordInteractionRequest {
  element: string;
  screen: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scheduled Loops (domain entity — cron-triggered agent tasks)
// ─────────────────────────────────────────────────────────────────────────────

/** A scheduled agent task. Fires on a cron schedule, creates Issues + Runs. */
/**
 * How a scheduled loop behaves when it fires:
 * - `mission`: create one issue from the loop's prompt and run it (the classic
 *   scheduled worker/PM). This is the default.
 * - `drain`: create no issue — instead kick the continuous build of the
 *   project's `ready` `implement` backlog, which then drains to empty. This is
 *   the "Engineer" that starts in the morning and works through the board.
 */
export const LoopMode = {
  Mission: 'mission',
  Drain: 'drain',
} as const;
export type LoopMode = (typeof LoopMode)[keyof typeof LoopMode];

export interface LoopDto {
  id: string;
  projectId: string;
  /** The owning project's key ("OMHB"), so a page holding a loop can link/fetch the project code-first. Null pre-backfill. */
  projectCode: string | null;
  name: string;
  description: string;
  /** Markdown brief given to the agent on each scheduled fire. */
  prompt: string;
  /** Standard 5-field cron expression (e.g. "0 0 * * 1" = every Monday UTC). */
  cronExpression: string;
  enabled: boolean;
  pipeline: AgentRole[];
  /** mission (default) files an issue per fire; drain kicks the implement backlog.
   *  The API always populates this; optional only so legacy fixtures still type. */
  mode?: LoopMode;
  /** Number of times this loop was skipped because a prior run was still active. */
  skipCount: number;
  lastFiredAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A loop enriched with its parent project's name + kind, for cross-project
 * listings (e.g. the Overview "Automations" section) that need to label and
 * route a loop without a second lookup.
 */
export interface LoopSummaryDto extends LoopDto {
  projectName: string;
  projectKind: ProjectKind;
  // projectCode is inherited from LoopDto.
}

// ---------------------------------------------------------------------------
// Cross-workspace dashboard ("/me/*")
//
// The entry layer — Overview, Projects, Prototypes — is GLOBAL: it aggregates
// across every workspace the caller is a member of, and you "enter" a workspace
// by opening one of its items (which routes to /w/<slug>/…). Each aggregated
// item carries the `workspace` it belongs to so the UI can group and link by
// slug. These endpoints are not org-scoped (no active workspace required).
// ---------------------------------------------------------------------------

/** A repo project on the global Projects page, tagged with its workspace and
 *  the per-project counts the list row shows (so the client needs no fan-out). */
export interface DashboardProjectDto extends ProjectDto {
  workspace: OrgSummary;
  /** Issues not in the Closed state. */
  openIssueCount: number;
  /** Active (provisioning/running) runs currently attached to the project. */
  activeRunCount: number;
}

/** A prototype on the global Prototypes page, tagged with its workspace. */
export interface DashboardPrototypeDto extends PrototypeDto {
  workspace: OrgSummary;
}

/** An issue on the global Overview, tagged with its workspace. */
export interface DashboardIssueDto extends IssueDto {
  workspace: OrgSummary;
}

/** A live/active run on the global Overview, tagged with its workspace. */
export interface DashboardActiveRunDto extends ActiveRunDto {
  workspace: OrgSummary;
}

/** A scheduled loop on the global Overview, tagged with its workspace. */
export interface DashboardLoopDto extends LoopSummaryDto {
  workspace: OrgSummary;
}

export type PatchKey = 'first-launch' | 'constellation' | 'century' | 'green-streak' | 'clean-week';
export const PATCH_ORDER: readonly PatchKey[] = ['first-launch', 'constellation', 'century', 'green-streak', 'clean-week'];

export interface HubVelocityDto {
  /** Median lead time (release -> merge) in minutes over issues shipped in the last 7 days; null if none. */
  leadMinutesMedian7d: number | null;
  shipped7d: number;
  /** The quickest of those, for the "fastest launch" line. */
  fastest7d: { issueCode: string | null; minutes: number } | null;
}

/**
 * One planet of the agent hub's solar system: a repo project OR a prototype
 * the caller can see, with the per-project counts the scene draws. Separate
 * from DashboardProjectDto because the overview's `projects` list is
 * repo-only (it feeds the issue/loop pickers), while the hub shows both kinds.
 */
export interface HubPlanetDto {
  id: string;
  name: string;
  code: string | null;
  kind: ProjectKind;
  workspace: OrgSummary;
  autonomy: ProjectAutonomy;
  /** Repo projects: false when no GitHub token is configured ("needs you"). */
  hasGithubCredential: boolean;
  createdAt: string;
  /** Issues not in the Closed state (exact count, not capped). */
  openIssueCount: number;
  /** Issues closed with resolution `fixed`, all time — the project's constellation. */
  shippedCount: number;
  /** Issues resting in `failed` (the retry budget gave up) — "needs you". */
  failedIssueCount: number;
  /** Prototype deploy state; null for repo projects. */
  live: { url: string; status: PrototypeDeployStatus } | null;
  velocity?: HubVelocityDto;
  patches?: PatchKey[];
}

/** Everything the global home/Overview renders, aggregated across the caller's
 *  workspaces. `projects` is repos only (prototypes have their own page). */
export interface DashboardOverviewDto {
  projects: DashboardProjectDto[];
  issues: DashboardIssueDto[];
  activeRuns: DashboardActiveRunDto[];
  loops: DashboardLoopDto[];
  /**
   * The activity feed across every workspace: the last N projects/prototypes
   * touched, each with its most-recent issues (newest group first).
   */
  recentActivity: RecentActivityGroupDto[];
  /** Agent-hub planets: every visible repo project AND prototype. */
  planets: HubPlanetDto[];
  /** Agent spend over the last 24 h, across visible projects only. */
  spend24hUsd: number;
  /** Issues closed `fixed` in the last 24 h, across visible projects only. */
  shipped24hCount: number;
}

/** One thing that happened, replayed by the hub's "while you were away". */
export type HubHistoryEventType = 'run.started' | 'pr.merged' | 'issue.arrived' | 'error.caught' | 'gate.failed' | 'gave.up';
export interface HubHistoryEvent {
  type: HubHistoryEventType;
  at: string;
  orgId: string;
  projectId: string;
  issueId?: string;
  issueCode?: string | null;
  runId?: string;
  lane?: 'plan' | 'build' | 'chat';
  prNumber?: number | null;
  prUrl?: string;
}
export interface HubHistoryDto {
  since: string;
  until: string;
  events: HubHistoryEvent[];
}

/** GET /projects/:id/pull-requests — an open pull request a run of this project opened. */
export interface ProjectPullRequestDto {
  url: string;
  number: number | null;
  title: string;
  issueCode: string | null;
  runId: string;
}

/** One shipped issue = one lit star in a project's constellation. */
export interface ConstellationStarDto {
  issueId: string;
  code: string | null;
  title: string;
  priority: number;
  /** ISO; the issue's updatedAt (no closedAt column exists). */
  shippedAt: string;
}

/** The most stars a constellation lights: the constellation endpoint returns that many, newest first. */
export const CONSTELLATION_MAX_STARS = 36;

/** GET /projects/:id/constellation — the latest 36 shipped issues (newest first) plus the true total. */
export interface ConstellationDto {
  projectId: string;
  shippedCount: number;
  stars: ConstellationStarDto[];
}

export type HubEventType =
  | 'issue.arrived'
  | 'issue.released'
  | 'error.caught'
  | 'run.started'
  | 'run.line'
  | 'run.settled'
  | 'pr.merged';

/**
 * The /hub gateway trims a run.line's text, then cuts it to this many chars. The web's feed id
 * normalises a polled `lastLogLine` the same way, so a line seen both ways is one feed entry.
 */
export const RUN_LINE_MAX_CHARS = 160;

/** Real-time /hub socket event. Carries no secrets; run.line text is a redacted display line. */
export interface HubEvent {
  type: HubEventType;
  orgId: string;
  projectId: string;
  at: string;
  runId?: string;
  issueId?: string;
  issueCode?: string | null;
  lane?: 'plan' | 'build' | 'chat';
  text?: string;
  prNumber?: number | null;
  prUrl?: string;
  /** Log-line kind/tool/detail, set on run activity events. */
  kind?: LogEntryKind;
  tool?: string;
  detail?: string;
}

/** One run entry in the loop health panel — most recent run per firing. */
export interface LoopRunSummary {
  runId: string;
  issueTitle: string;
  /** The issue's short code ("OMHB-42"), for display. Null pre-backfill. */
  issueCode: string | null;
  status: string;
  costUsd: number | null;
  createdAt: string;
  pullRequestUrl: string | null;
}

/** Aggregate health statistics for a scheduled loop. */
export interface LoopHealthDto {
  loopId: string;
  /** Total number of issues ever created by this loop. */
  firingCount: number;
  /** Number of scheduled fires skipped because a prior run was still active. */
  skipCount: number;
  /** Gate attempts that passed across all runs fired by this loop. */
  gatePassCount: number;
  /** Gate attempts that failed across all runs fired by this loop. */
  gateFailCount: number;
  /** Fraction of terminal runs that succeeded (null if no terminal runs). */
  successRate: number | null;
  /** Cumulative agent cost in USD across all runs. */
  totalCostUsd: number;
  /** Up to 10 most recent runs, newest first. */
  recentRuns: LoopRunSummary[];
}

export interface CreateLoopRequest {
  projectId: string;
  name: string;
  description?: string;
  prompt: string;
  cronExpression: string;
  enabled?: boolean;
  pipeline?: AgentRole[];
  /** mission (default) | drain. A drain loop needs no prompt/pipeline. */
  mode?: LoopMode;
}

export interface UpdateLoopRequest {
  name?: string;
  description?: string;
  prompt?: string;
  cronExpression?: string;
  enabled?: boolean;
  pipeline?: AgentRole[];
  mode?: LoopMode;
}

/** Result of manually firing a loop. */
export interface FireLoopResponse {
  issueId: string;
  runId: string;
}

/** One commit baked into the running build, shown in the version dropdown. */
export interface VersionCommit {
  /** Abbreviated commit hash (e.g. "f27e270"). */
  hash: string;
  /** Commit subject line. */
  title: string;
}

/** Deployed build identity — shown in the app so you can tell which version is
 *  live. `version` is `v1.<commit-count-on-main>`, or "dev" when not built by CI. */
export interface VersionDto {
  version: string;
  sha: string | null;
  builtAt: string | null;
  /** IANA timezone the platform uses for loop schedules (APP_TIMEZONE, default Europe/Amsterdam). */
  timezone: string;
  /** The last few commits in this build (newest first); empty when not built by CI. */
  commits: VersionCommit[];
}

/** First-run setup wizard. */
export interface SetupStatusDto {
  needsSetup: boolean;
}
export interface SetupRequest {
  email: string;
  password: string;
  /** Optional: seed provider/GitHub secrets during setup. */
  claudeOauthToken?: string;
  githubToken?: string;
}

/** Default loop pipeline: a single Worker stage. */
export const DEFAULT_PIPELINE: AgentRole[] = [AgentRole.Worker];

// ─────────────────────────────────────────────────────────────────────────────
// Runtime display-log protocol (agent container stdout → orchestrator)
// The runner prints normal log lines AND structured display-log lines. A
// display-log line begins with LOG_ENTRY_PREFIX followed by a JSON LogEntry.
// Authoritative run state travels on events.jsonl, not stdout.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stdout prefix for structured log entries (display data). Lines are
 * `LOG_ENTRY_PREFIX + JSON(LogEntry)`.
 */
export const LOG_ENTRY_PREFIX = '@@AIAI_E@@ ';

export type LogEntryKind =
  | 'prose' | 'thinking' | 'tool_use' | 'tool_result' | 'gate' | 'stage' | 'system';

export interface LogEntryMeta {
  /** tool_use only: tool name, e.g. "Bash", "Edit". */
  tool?: string;
  /** tool_use: formatted input summary; tool_result: first-line summary. */
  detail?: string;
  /** tool_result / gate failure. */
  isError?: boolean;
}

export interface LogEntry extends LogEntryMeta {
  kind: LogEntryKind;
  /** Plain-text fallback rendering — becomes LogLine.text. */
  text: string;
}

/** Context attached to a feedback submission: where the user was in the app. */
export interface FeedbackContext { path: string; label: string }

export interface CreateFeedbackRequest {
  body: string;
  context?: FeedbackContext;
}

/** An issue proposed by a research run, filed into the app backlog. */
export interface ProposedIssue {
  /** Container-local correlation id; referenced by a FeedbackAction.issueRef filed in the same run. */
  ref?: string;
  title: string;
  body: string;
  /** Topic tags for grouping (e.g. "backend", "auth"). */
  tags?: string[];
  /** When true, the issue is created buildable/ready-to-build; when false/omitted, it needs approval first. */
  approved?: boolean;
  /** When this issue is a decomposition child, the code/handle of the parent
   *  issue being split — MUST be the run's own assigned issue. Links the child
   *  back for "split from" and lets it inherit the parent's release disposition
   *  (build in auto / join the approval queue in review) instead of the
   *  net-new "always proposed" gate. */
  parentRef?: string;
  /** Factory prototypes only (AgentJobSpec.visitorTitle): a plain-language line
   *  for the app's visitors (trimmed, at most VISITOR_TITLE_MAX_LENGTH chars). */
  visitorTitle?: string;
}

/** One entry of the issue board handed to a worker (its to-do list). */
export interface BoardIssue {
  id: string;
  /** Human-facing stable code ("OMHB-42"). Cite this in commits/PRs/notes; the
   *  agent may also target issue/feedback/comment actions by code or by `id`. */
  code?: string | null;
  title: string;
  status: IssueStatus;
  /** True once the issue is buildable/ready-to-build; false while it still needs approval. */
  approved: boolean;
  /** Execution priority: 0 = none, 1 = low, 2 = medium, 3 = high. */
  priority: number;
  /** Topic tags for grouping (e.g. "backend", "auth"). */
  tags?: string[];
  /** Full brief/body — populated only for `research` items so the Planner can
   *  triage them (feedback text lives here). Omitted for ordinary board rows. */
  body?: string;
}

/** A worker's action on an existing app issue (e.g. mark it done). */
export interface IssueAction {
  /** The app issue this targets (an id from the board snapshot). */
  issueId: string;
  /** open|closed. Omit to leave the lifecycle status unchanged (e.g. an approve-only action). */
  status?: IssueStatus;
  /** Required when status===closed. fixed|wont_do|duplicate. NOT 'planned' (feedback-triage only). */
  resolution?: IssueResolution;
  /** For a duplicate close: the canonical issue this duplicates. */
  sourceIssueId?: string;
  /** Set the buildable/approval gate WITHOUT changing status (approve, or send back for planning). */
  approved?: boolean;
  /** Optional one-line note on why, surfaced in logs/activity. */
  note?: string;
  /** Replace the issue's topic tags (e.g. ["backend", "auth"]). Omit to leave tags unchanged. */
  tags?: string[];
}

/**
 * One entry of issue-comments.json: appends a note to an existing board issue
 * without changing its status. The agent writes this to `/aiai/issue-comments.json`.
 * Either `issueId` (board id) or `issueRef` (container-local ref from issues.json)
 * must be present.
 */
export interface IssueCommentAction {
  /** Board issue id to comment on (use this for existing issues). */
  issueId?: string;
  /** Container-local ref of a newly-filed issue (from issues.json). */
  issueRef?: string;
  /** The note text to append (plain text; max 4 KB). */
  note: string;
  /** True when this comment IS the PM's plan turn on its assigned proposed issue
   *  (vs a reply or clarifying question). When set, the projector runs the plan
   *  auto-release net (`decidePlanRelease`) and may flip the issue to Open on an
   *  `auto` project. Has no effect on Worker comments. */
  isPlan?: boolean;
  /** @deprecated Ignored since zero-touch autonomy (2026-09-27): autopilot plans
   *  always release. Still parsed so older agent images keep working. */
  hold?: boolean;
  /** @deprecated Ignored — see `hold`. */
  holdReason?: string;
  /** True when this comment is a BLOCKING clarifying question (not a plan): the
   *  Planner needs a human answer before it can plan. Mutually exclusive with
   *  `isPlan`. Surfaces the issue as "needs your answer" (assignee = you) and
   *  never auto-releases; a human reply wakes the Planner for its next turn. */
  question?: boolean;
  /** Factory prototypes only: sets/refreshes the issue's visitor-facing title
   *  (see ProposedIssue.visitorTitle), typically on the PM's plan comment. */
  visitorTitle?: string;
}

/**
 * A Planner triage decision for one feedback issue.
 * - 'planned': keep the feedback issue OPEN as the buildable task. Put the
 *   implementation plan in `note` (posted as a comment); `issueRef`/`issueId`
 *   are NOT used. The Worker builds the issue; its merged PR closes it.
 * - 'duplicate': close as duplicate of the issue named by `issueId` (+ `note`).
 * - 'dismissed': close as wont_do (+ short `note`).
 */
export interface FeedbackAction {
  /** ID of the source feedback issue on the board. */
  feedbackId: string;
  /** Whether the worker planned a proposal, marked it a duplicate, or dismissed this feedback item. */
  action: 'planned' | 'duplicate' | 'dismissed';
  /** Unused by any action; retained for shape compatibility only. */
  issueRef?: string;
  /** Board issue id of the duplicate target. Used only by the 'duplicate' action. */
  issueId?: string;
  /** Human-readable note surfaced in the issue timeline (reason for dismissal/duplicate, or proposal summary). */
  note?: string;
  /** @deprecated Ignored since zero-touch autonomy (2026-09-27): autopilot plans
   *  always release. Still parsed so older agent images keep working. */
  hold?: boolean;
  /** @deprecated Ignored — see `hold`. */
  holdReason?: string;
}

export type ControlEvent =
  | {
      kind: 'stage';
      role: AgentRole;
      order: number;
      status: StageStatus;
      summary?: string;
      /** The RUN's cumulative spend so far (USD), not this stage's own cost:
       *  readers take the latest / highest value, never a sum. */
      costUsd?: number;
      /** Gate failure output, present when status is 'awaiting_gate' or 'failed' via gate. */
      gateOutput?: string;
      /** Token usage from the Claude CLI result event. */
      inputTokens?: number;
      outputTokens?: number;
      cacheCreationTokens?: number;
      cacheReadTokens?: number;
    }
  | { kind: 'loop'; iteration: number; reason?: string }
  | { kind: 'branch'; branch: string }
  | { kind: 'pull_request'; url: string }
  | { kind: 'files'; files: ChangedFile[] }
  | { kind: 'issues'; issues: ProposedIssue[] }
  | { kind: 'feedback_actions'; actions: FeedbackAction[] }
  | { kind: 'issue_action'; action: IssueAction }
  | { kind: 'issue_comment'; comment: IssueCommentAction }
  | { kind: 'chat_reply'; reply: ChatReplyEntry }
  | { kind: 'result'; result: AgentRunResult }
  | { kind: 'high_turns'; role: AgentRole; order: number; turns: number }
  | {
      kind: 'stage_overrun';
      role: AgentRole;
      order: number;
      stageTimeoutSeconds: number;
    }
  | { kind: 'memory_update'; content: string }
  | { kind: 'wiki_update'; content: string }
  | { kind: 'storage_action'; enableStorage: true };

/**
 * A control event wrapped with a durable sequence number and timestamp.
 * This envelope, appended to /aiai/events.jsonl, is the sole authoritative run
 * event stream: the API consumes it live (and after a restart) through
 * RunEventSource.
 */
export interface ControlEnvelope {
  seq: number;
  ts: string;
  event: ControlEvent;
}

/**
 * Producer seam for the run control-event stream. The runtime writes events
 * through a sink; the sink stamps the durable seq + timestamp and persists the
 * envelope. `FileEventSink` (agent-runtime) appends to events.jsonl today; a
 * future RedisStreamEventSink XADDs the same envelope with no call-site change.
 */
export interface RunEventSink {
  /** Stamp + persist one control event. Never throws — persistence is best-effort. */
  append(event: ControlEvent): void;
}

/**
 * Consumer seam for the run control-event stream. The API reads events through
 * a source and never learns the transport. `FileEventSource` (api) tails
 * events.jsonl; a future RedisStreamEventSource consumer-group-reads the same
 * envelopes. `afterSeq` is an exclusive lower bound (0 = from the start).
 */
export interface RunEventSource {
  /** All envelopes with seq > afterSeq available right now, in seq order. */
  readFrom(runId: string, afterSeq: number): Promise<ControlEnvelope[]>;
  /** Live stream of envelopes with seq > afterSeq; ends after the terminal `result` event or when the underlying stream closes. */
  subscribe(runId: string, afterSeq: number): AsyncIterable<ControlEnvelope>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Observability capture protocol (agent container → /aiai/capture.jsonl → API)
// The runner appends one JSON CaptureRecord per attempt boundary; the API
// ingests the file at end-of-run into StageAttempt rows (one per loop iteration,
// carrying only the gate outcome). Independent of the live control/stdout path.
// ─────────────────────────────────────────────────────────────────────────────

export const CAPTURE_PATH_DEFAULT = '/aiai/capture.jsonl';

/** One line of /aiai/capture.jsonl. Written by the runner, ingested by the API. */
export type CaptureRecord =
  | {
      t: 'attempt_start';
      runId: string;
      order: number; // 1-based pipeline stage order
      iteration: number; // 0-based loop iteration
      ts: string; // ISO timestamp
    }
  | {
      t: 'attempt_end';
      runId: string;
      order: number;
      iteration: number;
      gatePassed?: boolean;
      ts: string;
    };


// ─────────────────────────────────────────────────────────────────────────────
// External-provider (GitHub) configuration errors
// A GitHub call can fail because the user's PAT is missing, invalid, or lacks a
// permission — a *configuration* problem, distinct from the user's own auth.
// The API tags these responses with a `code` (and, for permission problems, the
// `permission` the token needs) so the web can render an actionable "update your
// token in Settings" notice instead of a generic error — and, crucially, never
// returns HTTP 401 for them (a 401 would make the web think the session died).
// ─────────────────────────────────────────────────────────────────────────────

export const GithubErrorCode = {
  /** No GitHub token is configured at all. */
  TokenMissing: 'github_token_missing',
  /** GitHub rejected the token (their 401) — invalid or expired. */
  TokenInvalid: 'github_token_invalid',
  /** The token is valid but lacks a required permission (their 403). */
  TokenForbidden: 'github_token_forbidden',
  /** GitHub throttled the request — transient, not a config problem. */
  RateLimited: 'github_rate_limited',
  /** Any other upstream GitHub failure. */
  Upstream: 'github_upstream_error',
} as const;
export type GithubErrorCode =
  (typeof GithubErrorCode)[keyof typeof GithubErrorCode];

/** Codes the web surfaces as an actionable "configuration needed" notice. */
export const GITHUB_CONFIG_ERROR_CODES: readonly GithubErrorCode[] = [
  GithubErrorCode.TokenMissing,
  GithubErrorCode.TokenInvalid,
  GithubErrorCode.TokenForbidden,
];

/** Shape of an API error body that may carry a config `code` + `permission`. */
export interface ApiErrorBody {
  statusCode: number;
  error?: string;
  message: string | string[];
  /** Present for classified GitHub config errors (see GithubErrorCode). */
  code?: string;
  /** For permission errors: the GitHub permission the token needs (e.g. "Issues: Read"). */
  permission?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Platform admin DTOs (admin-only, no org context)
// ─────────────────────────────────────────────────────────────────────────────

/** User record with their personal workspace, for the admin user list. */
export interface AdminUserDto {
  id: string;
  email: string;
  createdAt: string;
  workspace?: { id: string; name: string; slug: string; memberCount: number };
}

/** Lifecycle of a workspace, derived from memberships + live invites — never stored. */
export type WorkspaceStatus = 'active' | 'invited' | 'unclaimed';

/** A pending (unaccepted, unexpired) invitation, as shown on the admin customers page. */
export interface AdminPendingInviteDto {
  id: string;
  invitedEmail: string;
  /** ISO timestamp. */
  expiresAt: string;
  /** ISO timestamp. */
  createdAt: string;
}

export interface AdminWorkspaceRowDto {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  owner: { id: string; email: string } | null;
  /** Newest live invite's email, when nobody has joined yet. */
  pendingInviteEmail: string | null;
  status: WorkspaceStatus;
  plan: OrgPlan;
  features: ResolvedFeatures;
  overrideCount: number;
  memberCount: number;
  prototypeCount: number;
  maxPrototypes: number | null;
  monthToDateSpendUsd: number;
  monthlySpendCapUsd: number | null;
  lastRunAt: string | null;
}
export type WorkspaceSortKey = 'name' | 'created' | 'lastRun' | 'spend';
export interface AdminWorkspaceListQuery {
  q?: string;
  plan?: OrgPlan;
  status?: WorkspaceStatus;
  /** `<featureKey>:on|off` */
  feature?: string;
  sort?: WorkspaceSortKey;
  dir?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}
export interface AdminWorkspaceDetailDto {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  status: WorkspaceStatus;
  owner: { id: string; email: string } | null;
  pendingInvites: AdminPendingInviteDto[];
  memberCount: number;
  plan: OrgPlan;
  featureOverrides: FeatureOverrides;
  maxPrototypesOverride: number | null;
  monthlySpendCapUsdOverride: number | null;
  maxConcurrency: number;
  maxRunCostUsd: number | null;
  entitlements: Entitlements;
  usage: EntitlementUsage;
}

/** Cross-org platform totals for the operator overview. */
export interface AdminOverviewDto {
  userCount: number;
  workspaceCount: number;
  activeRunCount: number;
  queuedRunCount: number;
  totalRunCount: number;
  totalCostUsd: number;
}

/**
 * Per-workspace snapshot for the root-admin workspace detail page: project/loop
 * counts, issue + run breakdowns, and the live ops signals (active/pending/stuck
 * runs). Every issue-state and run-status key is present, zero-filled.
 */
export interface AdminWorkspaceOverviewDto {
  repoCount: number;
  prototypeCount: number;
  loopCount: number;
  enabledLoopCount: number;
  /** Issue counts keyed by IssueState; every state present (0 default). */
  issuesByState: Record<IssueState, number>;
  /** Run counts keyed by RunStatus; every status present (0 default). */
  runsByStatus: Record<RunStatus, number>;
  /** Runs currently executing (status = running). */
  activeRunCount: number;
  /** Runs waiting for a slot (status = pending | provisioning). */
  pendingRunCount: number;
  /** Non-terminal runs past their timeout — a loop-wedge signal. */
  stuckRunCount: number;
}

/**
 * Lean prototype view for the root-admin workspace detail page: identity plus
 * the stored deployment state (live/stopped/…), so the operator can see which
 * previews are online without loading the full prototype module.
 */
export interface AdminWorkspacePrototypeDto {
  id: string;
  name: string;
  repoFullName: string;
  deployStatus: PrototypeDeployStatus;
  liveUrl: string;
  lastDeployedSha: string | null;
  lastError: string | null;
  /** ISO timestamp of the last deploy (deployment row's updatedAt). */
  lastDeployedAt: string;
  createdAt: string;
}

/**
 * Cross-workspace prototype entry for the admin fleet overview. Extends the
 * per-workspace view with workspace/project identity and live stats.
 */
export interface AdminPrototypeDto extends AdminWorkspacePrototypeDto {
  workspaceName: string;
  projectName: string;
  /** Live container stats; only populated when deployStatus === 'live'. */
  stats?: PrototypeStatsDto;
  /** Set when stats fetch failed; explains why the metric cells are empty. */
  statsError?: string;
}

/**
 * Instantaneous host-machine resource snapshot for the admin infrastructure view.
 */
export interface HostMetricsDto {
  cpu: { usagePercent: number };
  memory: { usedMb: number; totalMb: number; freeMb: number };
  disk: { usedGb: number; totalGb: number; freeGb: number };
  /** Seconds since the OS booted (Node os.uptime()). */
  uptime: number;
}

/** Presence flags for the global (non-BYO) operator secrets. */
export interface AdminSystemConfigDto {
  registryToken: boolean;
  registryUser: string;
  /** True when a platform-level Claude OAuth token is configured (DB or env). Orgs without their own token fall back to this. */
  platformClaudeToken: boolean;
  /** True when the platform GitHub App (id + private key + org) is configured. Shared-rail (basic) prototypes use it. */
  platformGithubApp: boolean;
  /** True when the server-side Claude API key (`LLM_API_KEY` Setting) is stored. Never the value. */
  llmApiKey: boolean;
  /** Monthly USD cap for server-side LLM features (`LLM_MONTHLY_BUDGET_USD`, default 200). */
  llmMonthlyBudgetUsd: number;
}

/** Default monthly cap (USD) for the server-side Claude API features when none is set. */
export const DEFAULT_LLM_MONTHLY_BUDGET_USD = 200;

/** Month-to-date spend for one server-side LLM feature. */
export interface LlmFeatureSpendDto {
  feature: string;
  calls: number;
  failures: number;
  costUsd: number;
}

/**
 * Ops view of the server-side Claude API module (`GET /admin/system/llm`),
 * read from the global `LlmUsage` table. The month is the UTC calendar month.
 */
export interface LlmStatusDto {
  keySet: boolean;
  budgetUsd: number;
  monthToDateUsd: number;
  /** ISO start of the current UTC month. */
  periodStart: string;
  byFeature: LlmFeatureSpendDto[];
  lastError: { feature: string; error: string; at: string } | null;
}

/** Safe view of the platform SMTP config — never includes the password itself. */
export interface SmtpConfigStatusDto {
  host: string;
  port: number;
  /** true = implicit TLS on connect (465); false = STARTTLS/plain (587/25). */
  secure: boolean;
  user: string;
  from: string;
  /** Whether a password is stored, without exposing it. */
  passwordConfigured: boolean;
}

/** Root-admin update of the platform SMTP config. `password` omitted/empty preserves the stored one. */
export interface PutSmtpConfigRequest {
  host: string;
  port: number;
  secure: boolean;
  user?: string;
  from: string;
  password?: string;
}

export interface CreateAdminUserRequest {
  email: string;
  password: string;
}

export interface UpdateAdminUserRequest {
  email?: string;
  password?: string;
}

/** PATCH /admin/workspaces/:id */
export interface UpdateWorkspaceRequest {
  name: string;
}
/** PATCH /admin/workspaces/:id/access (step-up) — null clears a feature override. */
export interface UpdateWorkspaceAccessRequest {
  plan?: OrgPlan;
  features?: FeatureOverridePatch;
}
/** PATCH /admin/workspaces/:id/limits (step-up) — quota sentinel convention. */
export interface UpdateWorkspaceLimitsRequest {
  maxPrototypesOverride?: number | null;
  monthlySpendCapUsdOverride?: number | null;
  maxConcurrency?: number;
  maxRunCostUsd?: number | null;
}
/** POST /admin/workspaces — a workspace on a chosen plan plus its owner invite. */
export interface AdminCreateWorkspaceRequest {
  email: string;
  workspaceName?: string;
  plan: OrgPlan;
  note?: string;
  expiresInHours?: number;
  features?: FeatureOverrides;
  maxPrototypesOverride?: number | null;
  monthlySpendCapUsdOverride?: number | null;
}
export interface AdminInviteResultDto {
  id: string;
  token: string;
  /** Tenant-app accept URL (built from the configured public URL, not the ops origin). */
  acceptUrl: string;
  expiresAt: string;
  /** False when SMTP is not configured — the link is still valid, send it by hand. */
  emailSent: boolean;
}
export interface AdminCreateWorkspaceResponse {
  workspace: AdminWorkspaceDetailDto;
  invite: AdminInviteResultDto;
}
/** POST /admin/workspaces/:id/invites */
export interface AdminInviteMemberRequest {
  email: string;
}
export type AdminWorkspaceTokenKind = 'integration' | 'mcp';
export interface AdminWorkspaceTokenDto {
  kind: AdminWorkspaceTokenKind;
  id: string;
  name: string;
  /** integration: workspace | project token; mcp: the holder. */
  scope: 'workspace' | 'project' | 'user';
  project: { id: string; name: string } | null;
  /** mcp: the holder. integration: null (the scope / project is the holder). */
  user: { id: string; email: string } | null;
  /**
   * Who minted it. mcp: the holder. integration: the creating account, or null
   * when unknown or that account was deleted (no FK, tokens outlive accounts).
   * There is no token prefix: only the hash is stored.
   */
  createdBy: { id: string; email: string } | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** A run currently active or queued, for the admin live activity view. */
export interface AdminActiveRunDto {
  id: string;
  issueId: string;
  issueTitle: string;
  /** The issue's short code ("OMHB-42"), for display. Null pre-backfill. */
  issueCode: string | null;
  workspaceId: string;
  workspaceName: string;
  projectId: string;
  repoFullName: string | null;
  status: string;
  currentStageRole: string | null;
  loopCount: number;
  startedAt: string | null;
  createdAt: string;
  pullRequestUrl: string | null;
}

/** A scheduled loop across all workspaces, for the admin scheduled view. */
export interface AdminScheduledLoopDto {
  id: string;
  name: string;
  description: string;
  workspaceId: string;
  workspaceName: string;
  projectId: string;
  projectName: string;
  /** Kind of the parent project ("repo" | "prototype") — used for routing. */
  projectKind: ProjectKind;
  cronExpression: string;
  enabled: boolean;
  pipeline: string[];
  lastFiredAt: string | null;
  nextRunAt: string | null;
  skipCount: number;
  createdAt: string;
}

/**
 * A live agent container observed directly on the Docker daemon, joined to
 * its Run row when one exists. Root-admin "backend inspector" — read-only.
 */
export interface LiveAgentDto {
  containerId: string;
  runId: string;
  containerState: string; // docker State e.g. "running"
  containerStatus: string; // docker Status e.g. "Up 3 minutes"
  startedAt: string; // ISO, from Created epoch secs
  ageSeconds: number;
  repoFullName: string | null;
  issueTitle: string | null;
  /** The issue's short code ("OMHB-42"), for display. Null pre-backfill / no DB row. */
  issueCode: string | null;
  /** The run's workspace slug, so the (cross-workspace) admin view can deep-link
   *  to `/w/<slug>/runs/<id>`. Null when the run's DB row is gone. */
  orgSlug: string | null;
  /** The run's issue id / workspace id, for deep links in the ops console. Null when absent. */
  issueId: string | null;
  orgId: string | null;
  runStatus: string | null; // RunStatus or null if no DB row
}

// ─────────────────────────────────────────────────────────────────────────────
// Org member management DTOs
// ─────────────────────────────────────────────────────────────────────────────

/** A single member of an org, as returned by the member management endpoints. */
export interface OrgMemberDto {
  /** Membership row id. */
  id: string;
  userId: string;
  email: string;
  role: 'owner' | 'member';
  createdAt: string;
}

/** A pending invitation to join an org workspace. */
export interface OrgInviteDto {
  id: string;
  orgId: string;
  invitedEmail: string;
  role: 'owner' | 'member';
  expiresAt: string;
  createdAt: string;
}

/** A single project grant to attach to a pending invite; materializes into a
 *  RoleAssignment for the invitee once they accept. */
export interface InviteGrantInput {
  projectId: string;
  role: ProjectRole;
}

/** Body of POST /orgs/:id/members/invite. */
export interface CreateInviteRequest {
  email: string;
  role?: 'owner' | 'member';
  /** Optional project grants to materialize once the invite is accepted. */
  grants?: InviteGrantInput[];
}

/** Body of PATCH /orgs/:id/members/:userId — promote / demote (owner only). */
export interface ChangeMemberRoleRequest {
  role: 'owner' | 'member';
}

/** Combined member list: resolved members + pending invites. */
export interface OrgMemberListDto {
  members: OrgMemberDto[];
  invites: OrgInviteDto[];
}

/** Response from POST /invitations/accept. */
export interface AcceptInviteResponse {
  orgId: string;
  orgSlug: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Project grant management DTOs (per-project RBAC)
// ─────────────────────────────────────────────────────────────────────────────

/** GET /me/grants — the caller's own project grants in the active org. */
export interface MyGrantDto {
  projectId: string;
  role: ProjectRole;
}

/** GET /projects/:id/grants — the members holding a grant on a project (owner-only). */
export interface ProjectGrantDto {
  userId: string;
  email: string;
  role: ProjectRole;
}

/** Body of PUT /projects/:id/grants/:userId. */
export interface UpsertGrantDto {
  role: ProjectRole;
}

/** Public invite preview — no auth required. */
export interface InvitePreviewDto {
  orgName: string;
  invitedEmail: string;
  expiresAt: string;
  /** True if a user account already exists for `invitedEmail` — the accept page
   *  then offers "sign in to accept" instead of the create-account form. */
  hasAccount: boolean;
}

/**
 * Body of `POST /issues/:id/run`. `costCapUsdOverride` is an org-owner-only,
 * one-off per-run cost cap (USD) for restarting an issue stopped by the
 * per-run cost cap; it applies to the new run only (retries and iterations
 * never inherit it).
 */
export interface RunIssueRequest {
  costCapUsdOverride?: number;
}

/** A Run again cost-cap override may be at most this many × the workspace cap. */
export const MAX_COST_CAP_OVERRIDE_MULTIPLIER = 10;

// ── Project transfer (move a project / prototype to another workspace) ─────

/**
 * Machine codes a project transfer is refused with (`code` on the 4xx body and
 * on each preflight refusal). Destination feature gaps reuse
 * `FEATURE_DISABLED_CODE` (with `feature`), the prototype cap reuses
 * `PROTOTYPE_LIMIT_REACHED_CODE`.
 */
export const ProjectTransferRefusalCode = {
  /** Source and destination are the same workspace. */
  SameWorkspace: 'SAME_WORKSPACE',
  /** The platform's own feedback project never moves. */
  SelfProject: 'SELF_PROJECT',
  /** A plan / build / chat run of the project is pending or running. */
  ActiveRun: 'ACTIVE_RUN',
  /** The destination already has a project for the same repository. */
  RepoTaken: 'REPO_ALREADY_IN_WORKSPACE',
  /** The destination already has a project with this issue-code prefix. */
  IssueCodeTaken: 'ISSUE_CODE_TAKEN',
  /** The destination's GitHub credentials cannot reach / push to the repo. */
  RepoInaccessible: 'REPO_INACCESSIBLE',
  /** A custom domain would collide with a workspace domain once moved. */
  DomainConflict: 'DOMAIN_CONFLICT',
} as const;

export interface ProjectTransferRefusal {
  /** A `ProjectTransferRefusalCode`, `FEATURE_DISABLED` or `PROTOTYPE_LIMIT_REACHED`. */
  code: string;
  message: string;
  /** HTTP status `POST /projects/:id/transfer` answers this refusal with. */
  status: 400 | 409;
  /** The missing destination feature, for `FEATURE_DISABLED`. */
  feature?: string;
}

export interface ProjectTransferPreflightDto {
  ok: boolean;
  refusals: ProjectTransferRefusal[];
}

/** One row of `GET /projects/:id/transfer/destinations` — another workspace the caller owns. */
export interface ProjectTransferDestinationDto {
  orgId: string;
  name: string;
  slug: string;
  plan: string;
  preflight: ProjectTransferPreflightDto;
}

/**
 * Body of `POST /projects/:id/transfer` — exactly one destination selector.
 * `toOrgId` names one of the caller's own workspaces (the destinations list);
 * `toOrgSlug` / `toEmail` (a user who owns exactly one other workspace) name
 * anyone's. An unknown slug / email is a generic 404.
 */
export interface TransferProjectRequest {
  toOrgId?: string;
  toOrgSlug?: string;
  toEmail?: string;
}

/** Machine codes of the pending hand-off (`ProjectTransfer`) endpoints. */
export const ProjectTransferRequestCode = {
  /** The body named no destination, or more than one selector. */
  DestinationRequired: 'DESTINATION_REQUIRED',
  /** `toEmail` owns more than one workspace: ask for `toOrgSlug` instead. */
  AmbiguousDestination: 'AMBIGUOUS_DESTINATION',
  /** `toOrgId` is not a workspace the caller owns. */
  DestinationNotOwned: 'DESTINATION_NOT_OWNED',
  /** The project already has a pending transfer (at most one). */
  AlreadyPending: 'TRANSFER_ALREADY_PENDING',
  /** The transfer was already accepted / declined / cancelled. */
  NotPending: 'TRANSFER_NOT_PENDING',
  /** The transfer's 7-day window passed. */
  Expired: 'TRANSFER_EXPIRED',
} as const;

/** How long a pending transfer waits for the destination before it expires. */
export const PROJECT_TRANSFER_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Response of `POST /projects/:id/transfer` and `POST /transfers/:id/accept`. */
export type ProjectTransferResultDto =
  /** Moved now: the caller owns both workspaces, or the destination accepted. */
  | { status: 'moved'; projectId: string; toOrgId: string }
  /** Waiting for an owner of the destination to accept (expires at `expiresAt`). */
  | { status: 'pending'; projectId: string; transferId: string; expiresAt: string };

export type ProjectTransferStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';

/** A side of a transfer as the OTHER workspace sees it: identity only. */
export interface ProjectTransferOrgRef {
  name: string;
  slug: string;
}

/** One row of `GET /transfers/incoming` / `GET /transfers/outgoing`. */
export interface ProjectTransferDto {
  id: string;
  status: ProjectTransferStatus;
  project: { id: string; name: string; kind: ProjectKind };
  fromOrg: ProjectTransferOrgRef;
  toOrg: ProjectTransferOrgRef;
  /** Who asked for it (email), as the destination sees it. */
  createdByEmail: string | null;
  createdAt: string;
  expiresAt: string;
}

export * from './ops';
export * from './health';
export * from './ops-console';
export * from './mcp-oauth';
